// ── CML Dashboard — Community Manager Local ──────────────────────────────
// Separate interface for local community managers.
// Access: role='cml' in AppContext (or via URL hash #cml + PIN)
// Privacy: aggregated stats only — no individual profile data

import { useState, useEffect, useMemo } from 'react';
import { useTr } from '../utils/useTr';
import { db, dbUpdate } from '../utils/supabase';
import { useApp } from '../contexts/AppContext';
// getMock* removed — data from Supabase
import QRScannerCamera from '../components/QRScannerCamera';
import { parseBeeLoveQR } from '../utils/qrScanner';
import CategoryTreePicker from '../components/CategoryTreePicker';
import { countMatches } from '../utils/categoryTree';
import { getEventTypes, formatEventDate } from '../utils/events';

// ── Revenue split constants ───────────────────────────────────────────────
const REV_SPLIT = {
  events:        { cml: 0.50, platform: 0.30, fund: 0.20 },
  sponsoring:    { cml: 0.65, platform: 0.35, fund: 0.00 },
  subscriptions: { cml: 0.10, platform: 0.85, fund: 0.05 },
  alacarte:      { cml: 0.15, platform: 0.80, fund: 0.05 },
};

// ── Mock CML data ─────────────────────────────────────────────────────────
const {} = {
  name: 'Marie Tremblay',
  network: 'ca_montreal',
  networkLabel: 'Montréal 🍁',
  stage: 'coordinator', // ambassador | coordinator | director
  joinedDate: '2024-09-01',
  revenue: {
    month: { events: 312.50, sponsoring: 195, subscriptions: 48.20, alacarte: 22.50 },
    total: { events: 1875, sponsoring: 780, subscriptions: 289.20, alacarte: 135 },
    pending: 187.50,
    nextPayoutDate: '2025-05-01',
  },
  stats: {
    membersTotal: 247,
    membersActive: 183,
    membersNew: 22,
    retention: 74,
    avgScore: 7.8,
    matchesMade: 89,
    eventsHosted: 8,
    conversionRate: 12,
  },
  sponsors: [
    { id:'sp1', name:'Bar Le Lab',       category:'Venue',     status:'active',    monthlyValue:150, logo:'🍺', contact:'marie@lelab.ca',    notes:'3 events booked for Q2' },
    { id:'sp2', name:'Café Resonance',   category:'Venue',     status:'active',    monthlyValue:100, logo:'☕', contact:'info@resonance.ca',  notes:'Speed dating partner' },
    { id:'sp3', name:'Yoga Plateau',     category:'Wellness',  status:'signed',    monthlyValue:80,  logo:'🧘', contact:'allo@yogaplateau.ca', notes:'Co-branding, 2 events/qtr' },
    { id:'sp4', name:'Resto Le Petit',   category:'F&B',       status:'contacted', monthlyValue:0,   logo:'🍽️', contact:'contact@lepetit.ca',  notes:'Meeting booked May 3' },
    { id:'sp5', name:'Vélo Montréal',    category:'Lifestyle', status:'prospect',  monthlyValue:0,   logo:'🚲', contact:'',                    notes:'Outdoor events potential' },
  ],
  budgetRequests: [
    { id:'br1', date:'2025-03-15', amount:500, reason:'Spring event venue deposit', status:'approved' },
    { id:'br2', date:'2025-04-02', amount:200, reason:'Social media ads — April', status:'pending' },
  ],
};

// ── Design tokens ─────────────────────────────────────────────────────────
const G = {
  bg:'#0A0A0F', card:'#13111C', border:'rgba(255,255,255,0.07)',
  white:'#F0EAD8', muted:'rgba(240,234,216,0.45)', gold:'#C9A84C',
  blue:'#5B9BD5', green:'#4EC994', red:'#E07070', purple:'#A084C9',
  sans:"'Outfit',sans-serif", serif:"'Cormorant Garamond',serif",
};

const STAGE_META = {
  ambassador:  { label:'Ambassador',   color:'#9CA880', next:'coordinator',  threshold:'200 members + 2 events/month' },
  coordinator: { label:'Coordinator',  color:G.gold,    next:'director',     threshold:'500 members + 4 events/month + 5 sponsors' },
  director:    { label:'Local Director', color:'#E2C06E', next:null,         threshold:null },
};

const STATUS_COLOR = { prospect:'rgba(255,255,255,0.3)', contacted:G.blue, signed:G.gold, active:G.green };

// ── Helpers ───────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1rem 1.1rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.3rem' }}>
        {icon && <span style={{ fontSize:'1rem' }}>{icon}</span>}
        <span style={{ fontSize:'0.6rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em' }}>{label}</span>
      </div>
      <div style={{ fontFamily:G.serif, fontSize:'2rem', fontWeight:400, color:color||G.gold, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:'0.65rem', color:G.muted, marginTop:'0.25rem' }}>{sub}</div>}
    </div>
  );
}


function generateTaxHTML(docType, year, cmlInfo, payer, fields, payouts, totalPaid) {
  const rows = (payouts||[]).map(p =>
    `<tr><td>${new Date(p.date).toLocaleDateString()}</td><td>${p.source}</td><td style="text-align:right">${parseFloat(p.amount).toFixed(2)} ${p.currency}</td></tr>`
  ).join('');
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${fields?.title||'Tax Document'} ${year}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 700px; margin: 40px auto; padding: 20px; color: #1a1a1a; }
    h1 { font-size: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
    h2 { font-size: 14px; color: #555; }
    .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .party { flex: 1; }
    .party h3 { font-size: 12px; text-transform: uppercase; color: #888; margin-bottom: 5px; }
    .fields { margin: 20px 0; }
    .field { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
    .field label { color: #555; font-size: 13px; }
    .field strong { font-size: 14px; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 13px; }
    th { background: #f5f5f5; padding: 8px; text-align: left; border: 1px solid #ddd; }
    td { padding: 7px 8px; border: 1px solid #eee; }
    .total { font-weight: bold; background: #f9f9f9; }
    .note { background: #f0f0f0; padding: 12px; border-radius: 6px; font-size: 12px; color: #555; margin-top: 20px; }
    .footer { margin-top: 40px; font-size: 11px; color: #999; border-top: 1px solid #ddd; padding-top: 15px; }
    @media print { body { margin: 0; } }
  </style></head><body>
  <h1>${fields?.title||'Statement of Payments'}</h1>
  <div class="header">
    <div class="party">
      <h3>Payer</h3>
      <strong>${payer?.name||'BeeLove Inc.'}</strong><br/>
      <span style="font-size:12px;color:#555">${payer?.address||''}</span><br/>
      ${payer?.taxId ? `<span style="font-size:12px;color:#555">Tax ID: ${payer.taxId}</span>` : ''}
    </div>
    <div class="party" style="text-align:right">
      <h3>Recipient</h3>
      <strong>${cmlInfo?.name||''}</strong><br/>
      ${cmlInfo?.businessName ? `<em style="font-size:12px">${cmlInfo.businessName}</em><br/>` : ''}
      <span style="font-size:12px;color:#555">${cmlInfo?.address||''}</span><br/>
      ${cmlInfo?.taxNumber ? `<span style="font-size:12px;color:#555">Tax ID: ${cmlInfo.taxNumber}</span>` : ''}
    </div>
  </div>
  <div class="fields">
    ${(fields?.fields||[]).map(f => `<div class="field"><label>${f.label}</label><strong>${f.value}</strong></div>`).join('')}
  </div>
  <h2>Payment detail</h2>
  <table>
    <thead><tr><th>Date</th><th>Source</th><th style="text-align:right">Amount</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr class="total"><td colspan="2"><strong>Total</strong></td><td style="text-align:right"><strong>${totalPaid?.toFixed(2)}</strong></td></tr></tfoot>
  </table>
  ${fields?.note ? `<div class="note">ℹ️ ${fields.note}</div>` : ''}
  <div class="footer">Generated by BeeLove on ${new Date().toLocaleDateString()} · This document is for tax declaration purposes only.</div>
  </body></html>`;
}

function SectionTitle({ children, action }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
      <h2 style={{ fontFamily:G.serif, fontSize:'1.4rem', fontWeight:400, color:G.white, margin:0 }}>{children}</h2>
      {action}
    </div>
  );
}

// ── Marketing PDF generator ───────────────────────────────────────────────
export function networkDisplayLabel(raw) {
  const MAP = {
    ca_montreal:'Montréal', ca_toronto:'Toronto', ca_vancouver:'Vancouver',
    ca_quebec:'Québec City', ca_ottawa:'Ottawa', ca_calgary:'Calgary',
    fr_paris:'Paris', fr_lyon:'Lyon', be_brussels:'Bruxelles',
    ch_zurich:'Zurich', us_newyork:'New York', us_miami:'Miami',
  };
  if (MAP[raw]) return MAP[raw];
  return (raw||'Ma ville').replace(/^[a-z]{2}_/i,'').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
}

function generateMarketingHTML(cml, networkUsers, config = {}) {
  const sec = (id) => config.sections ? config.sections[id] !== false : true;
  const opp = (id) => config.opps   ? config.opps[id]      !== false : true;
  const s = cml.stats || {};
  const netLabel    = networkDisplayLabel(cml.network || cml.networkLabel);
  const totalMonth  = Object.values(cml.revenue?.month || {}).reduce((a,b)=>a+b,0);
  const totalAllTime = Object.values(cml.revenue?.total || {}).reduce((a,b)=>a+b,0);
  const activeSponsors  = (cml.sponsors||[]).filter(sp=>sp.status==='active'||sp.status==='signed');
  const activeSponsorValue = activeSponsors.reduce((t,sp)=>t+(sp.monthly_value||sp.monthlyValue||0),0);
  const date = new Date().toLocaleDateString('fr-CA', { year:'numeric', month:'long', day:'numeric' });
  const stage = STAGE_META[cml.stage] || STAGE_META.ambassador;

  // Age distribution
  const u = networkUsers || [];
  const AGE_GROUPS = [{l:'18–24',min:18,max:24},{l:'25–29',min:25,max:29},{l:'30–35',min:30,max:35},{l:'36–44',min:36,max:44},{l:'45+',min:45,max:99}];
  const FB_AGES = [18,38,29,11,4];
  const ageGroups = AGE_GROUPS.map((g,i) => {
    const cnt = u.filter(x=>x.age>=g.min&&x.age<=g.max).length;
    return { label:g.l, pct: u.length>0 ? Math.round(cnt/u.length*100) : FB_AGES[i] };
  });

  // Gender
  const women = u.filter(x=>x.gender==='woman').length;
  const men   = u.filter(x=>x.gender==='man').length;
  const femPct = u.length>0 ? Math.round(women/u.length*100) : 58;
  const malPct = 100-femPct;

  // Interests
  const iCounts = {};
  u.forEach(x=>(x.interests||[]).forEach(i=>{iCounts[i]=(iCounts[i]||0)+1;}));
  const topI = Object.entries(iCounts).sort(([,a],[,b])=>b-a).slice(0,8);
  const fallI = [['Plein air & nature',42],['Gastronomie',38],['Musique',35],['Voyages',31],['Yoga & bien-être',28],['Lecture',24],['Photographie',20],['Cuisine',18]];
  const displayI = topI.length>=4 ? topI : fallI;

  // 6-month revenue trend (simulated multipliers if no history)
  const MULT = [0.42,0.55,0.63,0.74,0.88,1.00];
  const monthNames = Array.from({length:6},(_,i)=>{
    const d=new Date(); d.setMonth(d.getMonth()-(5-i));
    return d.toLocaleDateString('fr-CA',{month:'short'});
  });
  const trendBars = MULT.map((m,i)=>({ label:monthNames[i], val: Math.round(totalMonth*m) }));
  const trendMax  = Math.max(...trendBars.map(b=>b.val),1);

  // Member growth simulation
  const memberTrend = MULT.map((m,i)=>({ label:monthNames[i], val: Math.round((s.membersTotal||247)*m) }));

  // Images (Unsplash — professional, dark/upscale event aesthetic)
  const IMG = {
    cover:    'https://images.unsplash.com/photo-1514190051997-0f6f39ca5cde?w=1400&q=85',
    lounge:   'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=900&q=80',
    dining:   'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&q=80',
    social:   'https://images.unsplash.com/photo-1529543544282-ea669407fca3?w=900&q=80',
    cocktail: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=900&q=80',
    couple:   'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=900&q=80',
    venue:    'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=900&q=80',
  };

  const D = { bg:'#08070D', card:'#110F1A', border:'rgba(255,255,255,0.08)', gold:'#C9A84C', goldPale:'rgba(201,168,76,0.12)', white:'#F0EAD8', muted:'rgba(240,234,216,0.45)', green:'#4EC994', blue:'#5B9BD5', red:'#E07070' };

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>BeeLove · Kit Partenariat · ${netLabel}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*{box-sizing:border-box;margin:0;padding:0;}
html,body{background:#08070D;color:#F0EAD8;font-family:'Outfit',sans-serif;font-size:13px;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
a{color:#C9A84C;}
/* ── Cover ── */
.cover{position:relative;min-height:100vh;background:#08070D;display:flex;flex-direction:column;justify-content:flex-end;overflow:hidden;page-break-after:always;}
.cover-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.28;filter:saturate(0.7);}
.cover-overlay{position:absolute;inset:0;background:linear-gradient(to bottom,rgba(8,7,13,0.3) 0%,rgba(8,7,13,0.6) 50%,rgba(8,7,13,0.97) 100%);}
.cover-content{position:relative;z-index:2;padding:56px 60px;}
.cover-logo{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:300;color:#F0EAD8;letter-spacing:.04em;margin-bottom:6px;}
.cover-logo em{color:#C9A84C;font-style:italic;}
.cover-eyebrow{font-size:9px;letter-spacing:.28em;text-transform:uppercase;color:rgba(201,168,76,0.55);margin-bottom:48px;}
.cover-badge{display:inline-block;padding:4px 14px;background:rgba(201,168,76,0.1);border:1px solid rgba(201,168,76,0.3);border-radius:99px;font-size:9px;color:#C9A84C;font-weight:600;letter-spacing:.14em;text-transform:uppercase;margin-bottom:16px;}
.cover-title{font-family:'Cormorant Garamond',serif;font-size:52px;font-weight:300;line-height:1.08;color:#F0EAD8;margin-bottom:14px;}
.cover-title em{color:#C9A84C;font-style:italic;}
.cover-sub{font-size:15px;color:rgba(240,234,216,0.5);font-weight:300;margin-bottom:48px;letter-spacing:.01em;}
.cover-divider{width:100%;height:1px;background:linear-gradient(90deg,transparent,rgba(201,168,76,0.35),transparent);margin-bottom:32px;}
.cover-stats{display:flex;gap:48px;}
.cs-item{} .cs-val{font-family:'Cormorant Garamond',serif;font-size:36px;font-weight:400;color:#C9A84C;line-height:1;}
.cs-lbl{font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:rgba(240,234,216,0.4);margin-top:4px;}
.cover-footer{display:flex;justify-content:space-between;align-items:center;padding:24px 60px;border-top:1px solid rgba(255,255,255,0.07);position:relative;z-index:2;}
.cf-manager{font-size:11px;color:rgba(240,234,216,0.5);}
.cf-manager strong{color:#C9A84C;}
.cf-date{font-size:10px;color:rgba(240,234,216,0.3);}
/* ── Body ── */
.body{background:#08070D;padding:0;}
/* ── Section ── */
.sec{padding:52px 60px;border-bottom:1px solid rgba(255,255,255,0.06);}
.sec:last-child{border-bottom:none;}
.sec-eyebrow{font-size:9px;letter-spacing:.24em;text-transform:uppercase;color:rgba(201,168,76,0.5);margin-bottom:10px;}
.sec-title{font-family:'Cormorant Garamond',serif;font-size:34px;font-weight:300;color:#F0EAD8;margin-bottom:6px;line-height:1.15;}
.sec-title em{color:#C9A84C;font-style:italic;}
.sec-intro{font-size:13px;color:rgba(240,234,216,0.55);line-height:1.75;max-width:580px;margin-bottom:32px;}
/* ── Hero image strip ── */
.img-strip{display:grid;grid-template-columns:2fr 1fr 1fr;gap:8px;margin-bottom:32px;border-radius:12px;overflow:hidden;height:220px;}
.img-strip img{width:100%;height:100%;object-fit:cover;filter:saturate(0.85) brightness(0.9);}
/* ── Stat grid ── */
.stat-row{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:28px;}
.stat-card{background:#110F1A;border:1px solid rgba(255,255,255,0.07);border-radius:12px;padding:20px 16px;}
.sc-val{font-family:'Cormorant Garamond',serif;font-size:34px;font-weight:400;color:#C9A84C;line-height:1;margin-bottom:4px;}
.sc-lbl{font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:rgba(240,234,216,0.4);}
.sc-sub{font-size:10px;color:rgba(240,234,216,0.3);margin-top:3px;}
/* ── Two col ── */
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:28px;}
/* ── Bar rows ── */
.bar-row{display:flex;align-items:center;gap:10px;margin-bottom:9px;}
.br-lbl{width:52px;font-size:11px;color:rgba(240,234,216,0.5);}
.br-track{flex:1;height:6px;background:rgba(255,255,255,0.06);border-radius:3px;overflow:hidden;}
.br-fill{height:100%;background:linear-gradient(90deg,#C9A84C,#D4B870);border-radius:3px;}
.br-pct{width:30px;font-size:10px;color:rgba(240,234,216,0.4);text-align:right;}
/* ── KV list ── */
.kv-list{display:flex;flex-direction:column;gap:0;}
.kv{display:flex;justify-content:space-between;align-items:center;padding:9px 0;border-bottom:1px solid rgba(255,255,255,0.05);font-size:12px;}
.kv:last-child{border-bottom:none;}
.kv-k{color:rgba(240,234,216,0.45);}
.kv-v{font-weight:600;color:#F0EAD8;}
.kv-v.gold{color:#C9A84C;}
.kv-v.green{color:#4EC994;}
/* ── Interest tags ── */
.itag-wrap{display:flex;flex-wrap:wrap;gap:7px;}
.itag{padding:4px 12px;background:rgba(201,168,76,0.07);border:1px solid rgba(201,168,76,0.18);border-radius:99px;font-size:11px;color:rgba(240,234,216,0.7);}
.itag span{color:#C9A84C;font-weight:600;margin-left:4px;}
/* ── Trend chart (column bars) ── */
.chart-wrap{display:flex;align-items:flex-end;gap:6px;height:90px;margin-bottom:8px;}
.chart-col{flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;}
.chart-bar{width:100%;border-radius:3px 3px 0 0;background:linear-gradient(to top,#C9A84C,rgba(201,168,76,0.45));min-height:4px;}
.chart-lbl{font-size:9px;color:rgba(240,234,216,0.35);text-align:center;}
/* ── Opportunity cards ── */
.opp-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.opp{background:#110F1A;border:1px solid rgba(255,255,255,0.07);border-radius:12px;padding:20px;border-left-width:3px;}
.opp-icon{font-size:22px;margin-bottom:10px;}
.opp-title{font-size:14px;font-weight:600;color:#F0EAD8;margin-bottom:6px;}
.opp-desc{font-size:11px;color:rgba(240,234,216,0.5);line-height:1.6;margin-bottom:10px;}
.opp-est{font-size:11px;font-weight:600;color:#4EC994;}
/* ── Package cards ── */
.pkg-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;}
.pkg{border-radius:12px;overflow:hidden;border:1px solid rgba(255,255,255,0.08);}
.pkg-h{padding:22px 18px;}
.pkg-tier{font-size:9px;letter-spacing:.2em;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-bottom:6px;}
.pkg-name{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:300;color:#fff;margin-bottom:10px;}
.pkg-price{font-size:30px;font-weight:700;color:#fff;line-height:1;}
.pkg-per{font-size:10px;color:rgba(255,255,255,0.5);margin-top:3px;}
.pkg-b{background:#110F1A;padding:16px;}
.ben{display:flex;gap:8px;margin-bottom:8px;font-size:11px;color:rgba(240,234,216,0.6);line-height:1.4;}
.ben-check{color:#4EC994;font-weight:700;flex-shrink:0;}
/* ── Sponsor table ── */
table{width:100%;border-collapse:collapse;}
thead tr{border-bottom:1px solid rgba(255,255,255,0.1);}
th{font-size:9px;text-transform:uppercase;letter-spacing:.14em;color:rgba(240,234,216,0.35);padding:10px 12px;text-align:left;font-weight:500;}
tbody tr{border-bottom:1px solid rgba(255,255,255,0.05);}
td{padding:12px;font-size:12px;color:rgba(240,234,216,0.75);}
td strong{color:#F0EAD8;}
.sb{display:inline-block;padding:3px 9px;border-radius:99px;font-size:10px;font-weight:600;}
.sb-active{background:rgba(78,201,148,0.12);color:#4EC994;border:1px solid rgba(78,201,148,0.25);}
.sb-signed{background:rgba(201,168,76,0.12);color:#C9A84C;border:1px solid rgba(201,168,76,0.25);}
/* ── Revenue ── */
.rev-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:20px;}
.rev-card{background:#110F1A;border:1px solid rgba(255,255,255,0.07);border-radius:10px;padding:16px;}
.rev-src{font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:rgba(240,234,216,0.35);margin-bottom:6px;}
.rev-amt{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:400;color:#C9A84C;}
.rev-split{font-size:10px;color:rgba(240,234,216,0.3);margin-top:2px;}
/* ── Split bar ── */
.split-bar{display:flex;height:8px;border-radius:4px;overflow:hidden;margin:14px 0 8px;}
.split-cml{background:#C9A84C;}
.split-platform{background:#5B9BD5;}
.split-fund{background:#4EC994;}
.split-legend{display:flex;gap:16px;flex-wrap:wrap;}
.sl-item{display:flex;align-items:center;gap:5px;font-size:10px;color:rgba(240,234,216,0.5);}
.sl-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
/* ── CTA ── */
.cta{background:linear-gradient(135deg,#0D0B17,#1A1528);border:1px solid rgba(201,168,76,0.2);border-radius:16px;padding:44px 52px;text-align:center;margin:0 60px 60px;}
.cta-title{font-family:'Cormorant Garamond',serif;font-size:38px;font-weight:300;color:#F0EAD8;margin-bottom:12px;}
.cta-title em{color:#C9A84C;font-style:italic;}
.cta-sub{font-size:13px;color:rgba(240,234,216,0.5);line-height:1.8;margin-bottom:24px;max-width:500px;margin-left:auto;margin-right:auto;}
.cta-email{font-size:17px;font-weight:600;color:#C9A84C;margin-bottom:8px;}
.cta-name{font-size:11px;color:rgba(240,234,216,0.3);}
/* ── Footer ── */
.pdf-footer{display:flex;justify-content:space-between;padding:20px 60px 32px;font-size:9px;color:rgba(240,234,216,0.2);letter-spacing:.06em;}
/* ── Section image ── */
.sec-img{width:100%;height:200px;object-fit:cover;border-radius:12px;margin-bottom:28px;opacity:0.75;filter:saturate(0.8);}
.half-img{width:100%;height:160px;object-fit:cover;border-radius:10px;opacity:0.7;filter:saturate(0.8);}
/* ── Gender bar ── */
.gender-bar{display:flex;height:10px;border-radius:5px;overflow:hidden;margin:10px 0 5px;}
.gb-f{background:linear-gradient(90deg,#C9A84C,#D4B870);}
.gb-m{background:rgba(91,155,213,0.6);}
/* ── Print ── */
@media print{
  html,body{background:#08070D!important;}
  .cover{min-height:100vh;page-break-after:always;}
  .sec{page-break-inside:avoid;}
  .no-break{page-break-inside:avoid;}
}
</style>
</head>
<body>

<!-- ═══════════════════════════ COVER ═══════════════════════════ -->
<div class="cover">
  <img class="cover-img" src="${IMG.cover}" alt="" crossorigin="anonymous"/>
  <div class="cover-overlay"></div>
  <div class="cover-content">
    <div class="cover-logo"><em>Bee</em>Love</div>
    <div class="cover-eyebrow">Kit de Partenariat · Communauté</div>
    <div class="cover-badge">✦ ${stage.label} · ${netLabel}</div>
    <div class="cover-title">Partenariez avec<br><em>BeeLove ${netLabel}</em></div>
    <div class="cover-sub">Des connexions authentiques. Une audience engagée. Un impact local réel.</div>
    <div class="cover-divider"></div>
    <div class="cover-stats">
      <div class="cs-item"><div class="cs-val">${s.membersTotal||247}</div><div class="cs-lbl">Membres</div></div>
      <div class="cs-item"><div class="cs-val">${s.membersActive||183}</div><div class="cs-lbl">Actifs (30j)</div></div>
      <div class="cs-item"><div class="cs-val">${s.eventsHosted||8}</div><div class="cs-lbl">Événements</div></div>
      <div class="cs-item"><div class="cs-val">${activeSponsors.length||3}</div><div class="cs-lbl">Partenaires</div></div>
    </div>
  </div>
  <div class="cover-footer">
    <div class="cf-manager">Présenté par <strong>${cml.name||'BeeLove CML'}</strong> · Community Manager Local</div>
    <div class="cf-date">Document confidentiel · ${date}</div>
  </div>
</div>

<div class="body">

${sec('about')?`<!-- ═══════════════════════════ QUI SOMMES-NOUS ═══════════════════════════ -->
<div class="sec">
  <div class="sec-eyebrow">La plateforme</div>
  <div class="sec-title">Qu'est-ce que <em>BeeLove</em> ?</div>
  <div class="sec-intro">BeeLove est une application communautaire <strong style="color:#F0EAD8">sur invitation</strong> dédiée aux célibataires et jeunes professionnels qui recherchent des connexions authentiques — romantiques ou sociales. Contrairement aux applications de swipe, BeeLove construit des communautés locales soigneusement sélectionnées autour de valeurs, d'intérêts et d'un style de vie communs.</div>
  <div class="img-strip">
    <img src="${IMG.lounge}" alt="Événement BeeLove" crossorigin="anonymous"/>
    <img src="${IMG.dining}" alt="Dîner romantique" crossorigin="anonymous"/>
    <img src="${IMG.cocktail}" alt="Cocktails" crossorigin="anonymous"/>
  </div>
  <div class="two-col">
    <div>
      <div style="font-size:12px;color:rgba(240,234,216,0.7);line-height:1.75;margin-bottom:14px;">Chaque ville dispose d'un <strong style="color:#C9A84C">Community Manager Local (CML)</strong> qui organise des événements, des cercles privés et des activités de quartier. Votre partenariat est direct avec une personne locale — pas une plateforme anonyme.</div>
      <div style="display:flex;flex-direction:column;gap:8px;">
        ${[['✦','Communauté sur invitation — profils vérifiés'],['✦','Événements curatés en petits groupes'],['✦','Cercles thématiques par affinité'],['✦','Score de compatibilité intelligent']].map(([icon,text])=>`<div style="display:flex;gap:8px;font-size:11px;color:rgba(240,234,216,0.5);"><span style="color:#C9A84C">${icon}</span>${text}</div>`).join('')}
      </div>
    </div>
    <div>
      <img class="half-img" src="${IMG.social}" alt="Communauté" crossorigin="anonymous"/>
    </div>
  </div>
</div>

`:''}
${sec('community')?`<!-- ═══════════════════════════ COMMUNAUTÉ ═══════════════════════════ -->
<div class="sec">
  <div class="sec-eyebrow">Données réseau · ${netLabel}</div>
  <div class="sec-title">Notre <em>communauté</em></div>
  <div class="sec-intro">Une audience urbaine, active et engagée — des personnes qui investissent dans leur vie sociale et relationnelle.</div>

  <!-- KPI cards -->
  <div class="stat-row">
    <div class="stat-card"><div class="sc-val">${s.membersTotal||247}</div><div class="sc-lbl">Membres total</div><div class="sc-sub">+${s.membersNew||22} ce mois</div></div>
    <div class="stat-card"><div class="sc-val">${s.membersActive||183}</div><div class="sc-lbl">Membres actifs</div><div class="sc-sub">Sur 30 jours</div></div>
    <div class="stat-card"><div class="sc-val">${s.retention||74}%</div><div class="sc-lbl">Rétention</div><div class="sc-sub">Mensuelle</div></div>
    <div class="stat-card"><div class="sc-val">${s.matchesMade||89}</div><div class="sc-lbl">Connexions</div><div class="sc-sub">Matchs générés</div></div>
  </div>

  <!-- Evolution chart + demographics -->
  <div class="two-col">
    <div>
      <div style="font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:rgba(201,168,76,0.5);margin-bottom:12px;">Croissance des membres (6 mois)</div>
      <div class="chart-wrap">
        ${memberTrend.map(b=>`<div class="chart-col"><div class="chart-bar" style="height:${Math.round(b.val/Math.max(...memberTrend.map(x=>x.val),1)*82)}px"></div><div class="chart-lbl">${b.label}</div></div>`).join('')}
      </div>
      <div style="font-size:10px;color:rgba(240,234,216,0.3);margin-top:4px;">+${Math.round(((memberTrend[5].val-memberTrend[0].val)/Math.max(memberTrend[0].val,1))*100)}% sur 6 mois</div>
    </div>
    <div>
      <div style="font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:rgba(201,168,76,0.5);margin-bottom:12px;">Répartition par âge</div>
      ${ageGroups.map(g=>`<div class="bar-row"><span class="br-lbl">${g.label}</span><div class="br-track"><div class="br-fill" style="width:${g.pct}%"></div></div><span class="br-pct">${g.pct}%</span></div>`).join('')}
    </div>
  </div>

  <!-- Gender + community profile -->
  <div class="two-col">
    <div>
      <div style="font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:rgba(201,168,76,0.5);margin-bottom:12px;">Genre</div>
      <div class="gender-bar"><div class="gb-f" style="width:${femPct}%"></div><div class="gb-m" style="width:${malPct}%"></div></div>
      <div style="display:flex;gap:18px;font-size:10px;color:rgba(240,234,216,0.4);">
        <span><strong style="color:#C9A84C">${femPct}%</strong> Femmes</span>
        <span><strong style="color:#5B9BD5">${malPct}%</strong> Hommes</span>
      </div>
    </div>
    <div>
      <div style="font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:rgba(201,168,76,0.5);margin-bottom:12px;">Profil de la communauté</div>
      <div class="kv-list">
        <div class="kv"><span class="kv-k">Score moyen</span><span class="kv-v gold">${(s.avgScore||7.8).toFixed(1)}/10</span></div>
        <div class="kv"><span class="kv-k">Taux de match</span><span class="kv-v">${s.membersTotal>0?((s.matchesMade||89)/s.membersTotal*100).toFixed(0):36}% des membres</span></div>
        <div class="kv"><span class="kv-k">Nouveaux/mois</span><span class="kv-v green">+${s.membersNew||22}</span></div>
        <div class="kv"><span class="kv-k">Événements organisés</span><span class="kv-v">${s.eventsHosted||8}</span></div>
      </div>
    </div>
  </div>

  <!-- Interests -->
  <div style="font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:rgba(201,168,76,0.5);margin-bottom:12px;">Centres d'intérêt principaux</div>
  <div class="itag-wrap">
    ${displayI.map(([k,v])=>`<div class="itag">${String(k).replace(/^interest\./,'').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}<span>${v}</span></div>`).join('')}
  </div>
</div>

`:''}
${sec('events')?`<!-- ═══════════════════════════ ÉVÉNEMENTS ═══════════════════════════ -->
<div class="sec">
  <div class="sec-eyebrow">Activité · ${netLabel}</div>
  <div class="sec-title">Nos <em>événements</em></div>
  <div class="sec-intro">Des soirées intimement organisées qui créent de vraies connexions humaines — speed dating, apéros mixtes, ateliers, sorties nature, dîners thématiques.</div>
  <div class="img-strip" style="margin-bottom:28px;">
    <img src="${IMG.venue}" alt="Venue" crossorigin="anonymous"/>
    <img src="${IMG.couple}" alt="Rencontre" crossorigin="anonymous"/>
    <img src="${IMG.social}" alt="Social" crossorigin="anonymous"/>
  </div>
  <div class="stat-row">
    <div class="stat-card"><div class="sc-val">${s.eventsHosted||8}</div><div class="sc-lbl">Événements organisés</div><div class="sc-sub">Depuis le lancement</div></div>
    <div class="stat-card"><div class="sc-val">${Math.round((s.membersTotal||247)*0.72)}</div><div class="sc-lbl">Participants cumulés</div><div class="sc-sub">Toutes éditions</div></div>
    <div class="stat-card"><div class="sc-val">${s.conversionRate||12}%</div><div class="sc-lbl">Taux de conversion</div><div class="sc-sub">Vue → inscription</div></div>
    <div class="stat-card"><div class="sc-val">4.8/5</div><div class="sc-lbl">Satisfaction moyenne</div><div class="sc-sub">Évaluations membres</div></div>
  </div>
  <div style="font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:rgba(201,168,76,0.5);margin-bottom:14px;">Évolution du chiffre d'affaires (6 mois)</div>
  <div class="chart-wrap" style="height:100px;">
    ${trendBars.map(b=>`<div class="chart-col"><div class="chart-bar" style="height:${Math.round(b.val/trendMax*90)}px"></div><div class="chart-lbl">${b.label}</div></div>`).join('')}
  </div>
  <div style="font-size:10px;color:rgba(240,234,216,0.3);margin-top:4px;">Revenus CML mensuels estimés — tendance +${Math.round(((MULT[5]-MULT[0])/MULT[0])*100)}% sur 6 mois</div>
</div>

`:''}
${sec('opportunities')?`<!-- ═══════════════════════════ OPPORTUNITÉS ═══════════════════════════ -->
<div class="sec">
  <div class="sec-eyebrow">Partenariat</div>
  <div class="sec-title">Façons de nous <em>rejoindre</em></div>
  <div class="sec-intro">Des formats adaptés à chaque type d'entreprise, du bar de quartier à la marque nationale.</div>
  <div class="opp-grid">
    ${opp('venue_partner')?`<div class="opp" style="border-left-color:#C9A84C;"><div class="opp-icon">🏛️</div><div class="opp-title">Partenaire Venue</div><div class="opp-desc">Accueillez nos événements dans votre espace. Nous remplissons vos soirées creuses avec une audience sélectionnée. Vous gardez vos revenus F&B ; nous partageons la billetterie.</div><div class="opp-est">→ 400–1 200 $ de revenu par soirée</div></div>`:''}
    ${opp('event_sponsor')?`<div class="opp" style="border-left-color:#5B9BD5;"><div class="opp-icon">📣</div><div class="opp-title">Commanditaire d'Événement</div><div class="opp-desc">Votre marque sur les visuels, les notifications in-app et les réseaux sociaux. Touchez ${s.membersActive||183} membres actifs qui se déplacent en personne.</div><div class="opp-est">→ Dès 200 $/événement</div></div>`:''}
    ${opp('member_offer')?`<div class="opp" style="border-left-color:#4EC994;"><div class="opp-icon">🎯</div><div class="opp-title">Offre Membre Exclusive</div><div class="opp-desc">Rabais ou essai réservé aux membres BeeLove. Gyms, studios, restaurants — votre offre devant des personnes qui investissent activement dans leur vie sociale.</div><div class="opp-est">→ Affiliation ou forfait mensuel</div></div>`:''}
    ${opp('first_date')?`<div class="opp" style="border-left-color:#A084C9;"><div class="opp-icon">💑</div><div class="opp-title">Premier Rendez-vous Recommandé</div><div class="opp-desc">Dès qu'un match se crée entre deux membres, BeeLove recommande votre établissement comme lieu idéal pour leur premier rendez-vous. Audience ultra-ciblée, intention d'achat maximale, trafic qualifié mesurable via code QR.</div><div class="opp-est">→ Forfait mensuel · Visibilité garantie à chaque match</div></div>`:''}
    ${opp('circle_sponsor')?`<div class="opp" style="border-left-color:#E07070;"><div class="opp-icon">⭕</div><div class="opp-title">Parrainage de Cercle</div><div class="opp-desc">Devenez le partenaire officiel d'un BeeCircle thématique (Bien-être, Foodies, Plein air…). Votre logo dans le cercle, une offre exclusive pour ses membres, et une association naturelle avec une communauté d'affinité déjà engagée.</div><div class="opp-est">→ Forfait mensuel · Exclusivité par thématique</div></div>`:''}
    ${opp('cobranded')?`<div class="opp" style="border-left-color:#9CA880;"><div class="opp-icon">✨</div><div class="opp-title">Événement Co-Brandé</div><div class="opp-desc">Concevons ensemble un format sur mesure — yoga × speed dating, dégustation × conversation, atelier cuisine × mixer. Votre marque tissée dans une expérience authentique.</div><div class="opp-est">→ Conditions négociées</div></div>`:''}
  </div>
</div>`:''}

${sec('packages')?`<!-- ═══════════════════════════ PACKAGES ═══════════════════════════ -->
<div class="sec">
  <div class="sec-eyebrow">Forfaits de commandite</div>
  <div class="sec-title">Choisissez votre <em>niveau</em></div>
  <div class="sec-intro">Engagement mensuel · CAD · Minimum 3 mois</div>
  <div class="pkg-grid">
    <div class="pkg">
      <div class="pkg-h" style="background:linear-gradient(135deg,#2C1F0A,#3D2B0F);">
        <div class="pkg-tier">Bronze</div>
        <div class="pkg-name">Partenaire</div>
        <div class="pkg-price">200 $</div>
        <div class="pkg-per">par événement</div>
      </div>
      <div class="pkg-b">
        ${['Logo sur page & matériaux','1 mention réseaux/événement','Listing infolettre','2 billets gratuits'].map(b=>`<div class="ben"><span class="ben-check">✓</span>${b}</div>`).join('')}
      </div>
    </div>
    <div class="pkg">
      <div class="pkg-h" style="background:linear-gradient(135deg,#0D1929,#162540);">
        <div class="pkg-tier">Argent</div>
        <div class="pkg-name">Commanditaire</div>
        <div class="pkg-price">500 $</div>
        <div class="pkg-per">par mois</div>
      </div>
      <div class="pkg-b">
        ${['Tous les avantages Bronze','Kiosque à 2 événements/mois','Offre exclusive in-app','3 publications brandées/mois','Priorité de calendrier'].map(b=>`<div class="ben"><span class="ben-check">✓</span>${b}</div>`).join('')}
      </div>
    </div>
    <div class="pkg" style="border-color:rgba(201,168,76,0.3);">
      <div class="pkg-h" style="background:linear-gradient(135deg,#2A1E08,#C9A84C);">
        <div class="pkg-tier" style="display:flex;align-items:center;gap:8px;">Or <span style="background:rgba(255,255,255,0.2);padding:1px 8px;border-radius:99px;font-size:8px;">EXCLUSIF</span></div>
        <div class="pkg-name">Partenaire Or</div>
        <div class="pkg-price">1 200 $</div>
        <div class="pkg-per">par mois</div>
      </div>
      <div class="pkg-b">
        ${['Tous les avantages Argent','Exclusivité de catégorie','Droit de désignation événement','Histoire vedette mensuelle','Rapport de performance trimestriel','Introductions membres opt-in'].map(b=>`<div class="ben"><span class="ben-check">✓</span>${b}</div>`).join('')}
      </div>
    </div>
  </div>
</div>`:''}

${sec('partners')&&activeSponsors.length>0?`<!-- ═══════════════════════════ PARTENAIRES ACTUELS ═══════════════════════════ -->
<div class="sec">
  <div class="sec-eyebrow">Partenaires actifs · ${netLabel}</div>
  <div class="sec-title">Ils nous font <em>confiance</em></div>
  <table>
    <thead><tr><th>Partenaire</th><th>Catégorie</th><th>Statut</th><th>Valeur mensuelle</th></tr></thead>
    <tbody>
    ${activeSponsors.map(sp=>{
      const val = sp.monthly_value||sp.monthlyValue||0;
      return `<tr><td><strong>${sp.logo||sp.logo_url?`${sp.logo||''}`:''} ${sp.name}</strong></td><td style="color:rgba(240,234,216,0.45)">${sp.category||'—'}</td><td><span class="sb sb-${sp.status}">${sp.status==='active'?'Actif':'Signé'}</span></td><td>${val>0?`<strong style="color:#C9A84C">${val} $/mois</strong>`:'Partenariat'}</td></tr>`;
    }).join('')}
    </tbody>
  </table>
  <div style="margin-top:16px;font-size:11px;color:rgba(240,234,216,0.4);">Valeur totale des commandites actives : <strong style="color:#4EC994;">${activeSponsorValue} $/mois</strong></div>
</div>`:''}

${sec('revenue')?`<!-- ═══════════════════════════ REVENUS ═══════════════════════════ -->
<div class="sec">
  <div class="sec-eyebrow">Modèle économique</div>
  <div class="sec-title">Transparence des <em>revenus</em></div>
  <div class="sec-intro">Votre partenariat contribue directement au gestionnaire local. Voici la répartition de chaque source de revenus.</div>
  <div class="rev-grid">
    <div class="rev-card"><div class="rev-src">Événements — part CML ce mois</div><div class="rev-amt">$${((cml.revenue?.month?.events)||312).toFixed(0)}</div><div class="rev-split">50% des ventes de billets</div></div>
    <div class="rev-card"><div class="rev-src">Commandites — part CML ce mois</div><div class="rev-amt">$${((cml.revenue?.month?.sponsoring)||195).toFixed(0)}</div><div class="rev-split">65% des frais de commandite</div></div>
    <div class="rev-card"><div class="rev-src">Abonnements — part CML ce mois</div><div class="rev-amt">$${((cml.revenue?.month?.subscriptions)||48).toFixed(0)}</div><div class="rev-split">10% des abonnements réseau</div></div>
    <div class="rev-card" style="border-color:rgba(201,168,76,0.2)"><div class="rev-src">Cumul total CML</div><div class="rev-amt">$${totalAllTime.toFixed(0)}</div><div class="rev-split">Depuis le lancement</div></div>
  </div>
  <div style="font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:rgba(201,168,76,0.4);margin-bottom:8px;">Répartition type · Événements</div>
  <div class="split-bar">
    <div class="split-cml" style="width:50%"></div>
    <div class="split-platform" style="width:30%"></div>
    <div class="split-fund" style="width:20%"></div>
  </div>
  <div class="split-legend">
    <div class="sl-item"><div class="sl-dot" style="background:#C9A84C"></div>CML local · 50%</div>
    <div class="sl-item"><div class="sl-dot" style="background:#5B9BD5"></div>Plateforme BeeLove · 30%</div>
    <div class="sl-item"><div class="sl-dot" style="background:#4EC994"></div>Fonds communautaire · 20%</div>
  </div>
</div>`:''}


<!-- ═══════════════════════════ CTA ═══════════════════════════ -->
<div class="cta">
  <div class="cta-title">Construisons quelque chose <em>ensemble</em></div>
  <div class="cta-sub">BeeLove ${netLabel} cherche des partenaires qui partagent nos valeurs :<br>authenticité, communauté locale, et connexion humaine sincère.</div>
  <div class="cta-email">✉ ${cml.email||'contact@beelove.me'}</div>
  ${cml.name?`<div class="cta-name">${cml.name} · Community Manager Local · BeeLove ${netLabel}</div>`:''}
</div>

<div class="pdf-footer">
  <span>BeeLove · Kit de Partenariat · ${netLabel} · Confidentiel</span>
  <span>Généré le ${date} · beelove.me</span>
</div>

</div><!-- /body -->
</body>
</html>`;
}

// ── Tab: Marketing Kit ────────────────────────────────────────────────────
const PDF_SECTIONS = [
  { id:'about',         icon:'💬', label:'Qui sommes-nous',          desc:'Présentation de BeeLove et du rôle du CML', required: false, defaultOn: true },
  { id:'community',     icon:'🐝', label:'Notre communauté',         desc:'Stats membres, démographie, croissance, centres d\'intérêt', required: false, defaultOn: true },
  { id:'events',        icon:'🎉', label:'Nos événements',           desc:'Historique événements, fréquentation, tendance de revenus', required: false, defaultOn: true },
  { id:'opportunities', icon:'🤝', label:'Opportunités de partenariat', desc:'Les 6 formats de collaboration disponibles', required: false, defaultOn: true },
  { id:'packages',      icon:'📦', label:'Forfaits de commandite',   desc:'Bronze / Argent / Or avec avantages et tarifs', required: false, defaultOn: true },
  { id:'partners',      icon:'✅', label:'Partenaires actuels',      desc:'Table des sponsors actifs et signés', required: false, defaultOn: false },
  { id:'revenue',       icon:'📈', label:'Modèle de revenus',        desc:'Transparence sur la répartition CML / Plateforme / Fonds', required: false, defaultOn: false },
];

const OPP_OPTIONS = [
  { id:'venue_partner',  icon:'🏛️', label:'Partenaire Venue' },
  { id:'event_sponsor',  icon:'📣', label:'Commanditaire d\'événement' },
  { id:'member_offer',   icon:'🎯', label:'Offre membre exclusive' },
  { id:'first_date',     icon:'💑', label:'Premier rendez-vous recommandé' },
  { id:'circle_sponsor', icon:'⭕', label:'Parrainage de Cercle' },
  { id:'cobranded',      icon:'✨', label:'Événement co-brandé' },
];

export function TabMarketing({ cml, networkUsers }) {
  const [generating, setGenerating] = useState(false);

  const defaultSections = Object.fromEntries(PDF_SECTIONS.map(s => [s.id, s.defaultOn]));
  const [sections, setSections] = useState(defaultSections);
  const toggleSection = (id) => setSections(p => ({ ...p, [id]: !p[id] }));

  const defaultOpps = Object.fromEntries(OPP_OPTIONS.map(o => [o.id, true]));
  const [opps, setOpps] = useState(defaultOpps);
  const toggleOpp = (id) => setOpps(p => ({ ...p, [id]: !p[id] }));

  const activeSponsors = (cml.sponsors||[]).filter(s=>s.status==='active');
  const enabledCount = Object.values(sections).filter(Boolean).length + 1; // +1 for cover always

  const openPDF = () => {
    setGenerating(true);
    setTimeout(() => {
      const html = generateMarketingHTML(cml, networkUsers, { sections, opps });
      const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) { alert('Autorisez les popups pour générer le PDF.'); URL.revokeObjectURL(url); setGenerating(false); return; }
      setTimeout(() => { w.print(); URL.revokeObjectURL(url); setGenerating(false); }, 600);
    }, 100);
  };

  const rowStyle = (on) => ({
    display:'flex', alignItems:'center', gap:'0.7rem', padding:'0.6rem 0.8rem',
    borderRadius:8, cursor:'pointer', background: on ? 'rgba(201,168,76,0.07)' : 'transparent',
    border: `1px solid ${on ? 'rgba(201,168,76,0.2)' : G.border}`,
    transition:'all 0.15s',
  });

  const toggle = (on) => ({
    width:36, height:20, borderRadius:10, position:'relative', flexShrink:0,
    background: on ? G.gold : 'rgba(255,255,255,0.1)', transition:'background 0.2s',
  });
  const dot = (on) => ({
    position:'absolute', top:3, left: on ? 19 : 3, width:14, height:14,
    borderRadius:'50%', background:'#fff', transition:'left 0.2s',
  });

  return (
    <div>
      <SectionTitle>Kit de partenariat PDF</SectionTitle>

      {/* Header preview */}
      <div style={{ background:'linear-gradient(145deg,#0A0A0F,#1A1420)', border:'1px solid rgba(201,168,76,0.2)', borderRadius:14, padding:'1.2rem 1.4rem', marginBottom:'1.2rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontFamily:G.serif, fontSize:'1.5rem', color:G.white }}><em style={{ color:G.gold }}>Bee</em>Love {networkDisplayLabel(cml.network||cml.networkLabel)}</div>
          <div style={{ fontSize:'0.68rem', color:G.muted, marginTop:'0.2rem' }}>Kit de partenariat · {enabledCount} section{enabledCount>1?'s':''} sélectionnée{enabledCount>1?'s':''}</div>
        </div>
        <div style={{ display:'flex', gap:'1rem' }}>
          {[{v:cml.stats?.membersTotal||0,l:'Membres'},{v:cml.stats?.eventsHosted||0,l:'Événements'},{v:activeSponsors.length,l:'Partenaires'}].map(x=>(
            <div key={x.l} style={{ textAlign:'center' }}>
              <div style={{ fontFamily:G.serif, fontSize:'1.4rem', color:G.gold }}>{x.v}</div>
              <div style={{ fontSize:'0.55rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em' }}>{x.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section selector */}
      <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ fontSize:'0.6rem', textTransform:'uppercase', letterSpacing:'0.14em', color:G.muted, marginBottom:'0.75rem' }}>
          Sections à inclure
        </div>

        {/* Cover — always on */}
        <div style={{ ...rowStyle(true), opacity:0.5, marginBottom:'0.4rem', cursor:'default' }}>
          <div style={toggle(true)}><div style={dot(true)}/></div>
          <span style={{ fontSize:'1rem' }}>🖼️</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'0.75rem', color:G.white, fontWeight:600 }}>Page de couverture</div>
            <div style={{ fontSize:'0.62rem', color:G.muted }}>Toujours incluse — titre, statistiques clés, gestionnaire</div>
          </div>
          <span style={{ fontSize:'0.6rem', color:G.muted }}>Fixe</span>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
          {PDF_SECTIONS.map(sec => {
            const on = sections[sec.id];
            return (
              <div key={sec.id} onClick={() => toggleSection(sec.id)} style={rowStyle(on)}>
                <div style={toggle(on)}><div style={dot(on)}/></div>
                <span style={{ fontSize:'1rem' }}>{sec.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'0.75rem', color: on ? G.white : G.muted, fontWeight: on ? 600 : 400 }}>{sec.label}</div>
                  <div style={{ fontSize:'0.62rem', color:G.muted }}>{sec.desc}</div>
                </div>
                {sec.id === 'partners' && !activeSponsors.length && (
                  <span style={{ fontSize:'0.58rem', color:G.muted, fontStyle:'italic' }}>Aucun partenaire actif</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Opportunities sub-selector */}
        {sections.opportunities && (
          <div style={{ marginTop:'0.8rem', padding:'0.8rem', background:'rgba(255,255,255,0.03)', borderRadius:8, border:`1px solid ${G.border}` }}>
            <div style={{ fontSize:'0.58rem', textTransform:'uppercase', letterSpacing:'0.12em', color:G.muted, marginBottom:'0.6rem' }}>
              Types de partenariat à afficher
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.35rem' }}>
              {OPP_OPTIONS.map(o => {
                const on = opps[o.id];
                return (
                  <div key={o.id} onClick={() => toggleOpp(o.id)}
                    style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.3rem 0.5rem', borderRadius:6, cursor:'pointer', background: on ? 'rgba(201,168,76,0.06)' : 'transparent', border:`1px solid ${on ? 'rgba(201,168,76,0.15)' : 'transparent'}` }}>
                    <div style={{ width:28, height:16, borderRadius:8, position:'relative', flexShrink:0, background: on ? G.gold : 'rgba(255,255,255,0.08)' }}>
                      <div style={{ position:'absolute', top:2, left: on ? 14 : 2, width:12, height:12, borderRadius:'50%', background:'#fff', transition:'left 0.15s' }}/>
                    </div>
                    <span style={{ fontSize:'0.72rem' }}>{o.icon}</span>
                    <span style={{ fontSize:'0.65rem', color: on ? G.white : G.muted }}>{o.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Generate button */}
      <button onClick={openPDF} disabled={generating}
        style={{ width:'100%', padding:'0.9rem', background: generating ? 'rgba(201,168,76,0.4)' : 'linear-gradient(135deg,#C9A84C,#D4A84C)', border:'none', borderRadius:10, color:'#0A0A0A', fontFamily:G.sans, fontSize:'0.9rem', fontWeight:700, cursor: generating ? 'wait' : 'pointer', letterSpacing:'0.04em' }}>
        {generating ? '⏳ Génération en cours…' : `📄 Générer le PDF · ${enabledCount} section${enabledCount>1?'s':''}`}
      </button>
      <p style={{ fontSize:'0.62rem', color:G.muted, textAlign:'center', marginTop:'0.5rem' }}>
        Ouvre le dialogue d'impression · Choisir "Enregistrer en PDF"
      </p>

      {/* Tips */}
      <div style={{ marginTop:'1rem', padding:'0.8rem 1rem', background:'rgba(91,155,213,0.06)', border:'1px solid rgba(91,155,213,0.12)', borderRadius:10 }}>
        <div style={{ fontSize:'0.6rem', textTransform:'uppercase', letterSpacing:'0.1em', color:G.blue, marginBottom:'0.5rem' }}>💡 Conseils d'utilisation</div>
        {[
          'Début de lancement : inclure uniquement Couverture + Communauté + Opportunités',
          'Activer "Partenaires actuels" seulement quand vous avez ≥ 2 partenaires signés',
          'Activer "Revenus" uniquement pour des partenaires de confiance déjà engagés',
          'Désactiver les types de partenariat non encore disponibles dans votre réseau',
        ].map(tip => (
          <div key={tip} style={{ fontSize:'0.68rem', color:G.muted, display:'flex', gap:'0.5rem', marginBottom:'0.3rem' }}>
            <span style={{ color:G.blue, flexShrink:0 }}>→</span>{tip}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Dashboard ────────────────────────────────────────────────────────
function TabDashboard({ cml, tr, taxYear, setTaxYear, generateTaxReport, taxLoading }) {
  const totalMonth = Object.values(cml.revenue.month).reduce((a,b)=>a+b,0);
  const totalEarned = Object.values(cml.revenue.total).reduce((a,b)=>a+b,0);
  const stage = STAGE_META[cml.stage];
  const progress = cml.stage === 'coordinator'
    ? Math.min(100, Math.round((cml.stats.membersTotal / 500) * 100))
    : cml.stage === 'ambassador'
    ? Math.min(100, Math.round((cml.stats.membersTotal / 200) * 100))
    : 100;


    return (
    <div>
      {/* Stage badge */}
      <div style={{ background:G.card, border:`1px solid ${stage.color}44`, borderRadius:12, padding:'1rem 1.2rem', marginBottom:'1.2rem', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
        <div>
          <div style={{ fontSize:'0.58rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.2rem' }}>Your stage</div>
          <div style={{ fontFamily:G.serif, fontSize:'1.5rem', color:stage.color }}>{stage.label}</div>
          <div style={{ fontSize:'0.68rem', color:G.muted, marginTop:'0.15rem' }}>{cml.networkLabel}</div>
        </div>
        {stage.next && (
          <div style={{ flex:1, maxWidth:200 }}>
            <div style={{ fontSize:'0.6rem', color:G.muted, marginBottom:'0.35rem' }}>Progress → {STAGE_META[stage.next].label}</div>
            <div style={{ height:5, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${progress}%`, background:stage.color, borderRadius:3, transition:'width 0.5s' }}/>
            </div>
            <div style={{ fontSize:'0.6rem', color:G.muted, marginTop:'0.2rem' }}>{progress}% · {stage.threshold}</div>
          </div>
        )}
        {!stage.next && (
          <div style={{ padding:'0.4rem 0.9rem', background:`${stage.color}22`, border:`1px solid ${stage.color}44`, borderRadius:99 }}>
            <span style={{ fontSize:'0.68rem', color:stage.color, fontWeight:700 }}>✦ Max level</span>
          </div>
        )}
      </div>

      {/* Revenue cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.7rem', marginBottom:'1.2rem' }}>
        <StatCard label={tr('cml.revenue_month')} value={`$${totalMonth.toFixed(0)}`} sub="CAD this month" color={G.gold} icon="💰"/>
        <StatCard label={tr('cml.revenue_total')} value={`$${totalEarned.toFixed(0)}`} sub="All time" color={G.green} icon="📈"/>
        <StatCard label={tr('cml.revenue_pending')} value={`$${cml.revenue.pending}`} sub={`Payout: ${cml.revenue.nextPayoutDate}`} color={G.muted} icon="⏳"/>
        <StatCard label={tr('cml.members_active')} value={cml.stats.membersActive} sub={`+${cml.stats.membersNew} this month`} color={G.purple} icon="🐝"/>
      </div>

      {/* Revenue breakdown */}
      <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1rem', marginBottom:'1.2rem' }}>
        <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.8rem' }}>{tr('cml.breakdown')} — this month</div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
          <p style={{ fontSize:'0.68rem', fontWeight:600, letterSpacing:'0.1em', color:'var(--muted)', textTransform:'uppercase', margin:0 }}>Revenue breakdown</p>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <select value={taxYear} onChange={e => setTaxYear(Number(e.target.value))}
              style={{ padding:'3px 8px', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:6, color:'var(--muted)', fontSize:'0.7rem', cursor:'pointer', fontFamily:'Outfit' }}>
              {[0,1,2].map(offset => {
                const y = new Date().getFullYear() - offset;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
            <button onClick={generateTaxReport} disabled={taxLoading}
              style={{ padding:'4px 10px', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:6, color:'var(--gold)', fontSize:'0.7rem', cursor:'pointer', fontFamily:'Outfit', fontWeight:600 }}>
              {taxLoading ? 'Generating…' : '📄 Tax report'}
            </button>
          </div>
        </div>
        {Object.entries(cml.revenue.month).map(([key, amount]) => {
          const labels = { events:tr('cml.rev_events'), sponsoring:tr('cml.rev_sponsors'), subscriptions:tr('cml.rev_subscriptions'), alacarte:tr('cml.rev_alacarte') };
          const split = REV_SPLIT[key];
          const gross = split ? Math.round(amount / split.cml) : amount;
        
    return (
            <div key={key} style={{ display:'flex', alignItems:'center', gap:'0.7rem', marginBottom:'0.6rem' }}>
              <div style={{ width:90, fontSize:'0.72rem', color:G.muted, flexShrink:0 }}>{labels[key]}</div>
              <div style={{ flex:1, height:6, background:'rgba(255,255,255,0.05)', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${totalMonth>0?Math.round((amount/totalMonth)*100):0}%`, background:G.gold, borderRadius:3 }}/>
              </div>
              <div style={{ width:60, textAlign:'right', fontSize:'0.75rem', color:G.white, fontWeight:600 }}>${amount.toFixed(0)}</div>
              <div style={{ width:50, textAlign:'right', fontSize:'0.6rem', color:G.muted }}>of ${gross}</div>
            </div>
          );
        })}
      </div>

      {/* Quick stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.7rem', marginBottom:'1.2rem' }}>
        <StatCard label={tr('cml.stats_matches')} value={cml.stats.matchesMade} icon="💞" color={G.purple}/>
        <StatCard label={tr('cml.events_hosted')} value={cml.stats.eventsHosted} icon="🎉" color={G.blue}/>
        <StatCard label={tr('cml.stats_retention')} value={`${cml.stats.retention}%`} icon="🔄" color={G.green}/>
      </div>

      {/* Revenue split explainer */}
      <div style={{ background:'rgba(91,155,213,0.06)', border:`1px solid rgba(91,155,213,0.15)`, borderRadius:10, padding:'0.9rem 1rem' }}>
        <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.6rem' }}>Revenue split model</div>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
          {Object.entries(REV_SPLIT).map(([source, split]) => (
            <div key={source} style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.68rem' }}>
              <span style={{ width:100, color:G.muted }}>{ ({ events:'Events', sponsoring:'Sponsoring', subscriptions:'Subscriptions', alacarte:'À la carte' })[source]}</span>
              <span style={{ color:G.green, fontWeight:700, width:30 }}>{Math.round(split.cml*100)}%</span>
              <div style={{ flex:1, height:4, background:'rgba(255,255,255,0.05)', borderRadius:2, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${Math.round(split.cml*100)}%`, background:G.green, borderRadius:2 }}/>
              </div>
              <span style={{ color:G.muted, fontSize:'0.6rem' }}>you · {Math.round(split.platform*100)}% platform{split.fund>0?` · ${Math.round(split.fund*100)}% fund`:''}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Tab: Events ───────────────────────────────────────────────────────────
function TabEvents({ cml, lang, tr }) {
  const [showQR, setShowQR] = useState(false);
  const [qrInput, setQrInput] = useState('');
  const [qrResult, setQrResult] = useState(null);
  const [attendanceCounts, setAttendanceCounts] = useState({});

  const eventsFiltered = (cml.events || []).filter(e => e.network === cml.network || e.status === 'past');
  const types = getEventTypes(lang);

  const validateQR = async (qrValue) => {
    if (!qrValue?.trim()) return;
    // QR format: beelove:event:{eventId}:{qrToken}
    const parts = qrValue.split(':');
    const isValid = parts[0] === 'beelove' && parts[1] === 'event' && parts.length >= 4;
    if (!isValid) { setQrResult({ valid: false, error: 'Invalid QR format' }); return; }

    const eventId  = parts[2];
    const qrToken  = parts[3];

    try {
      const { db } = await import('../utils/supabase');
      // Validate ticket — find registration by qr_token
      const { data: reg, error } = await db.event_registrations()
        ?.select('user_id, confirmed, attended, user:user_core!user_id(name, email)')
        .eq('event_id', eventId)
        .eq('qr_token', qrToken)
        .single();

      if (error || !reg) {
        setQrResult({ valid: false, error: 'Ticket not found' });
      } else if (reg.attended) {
        setQrResult({ valid: false, error: 'Already scanned', name: reg.user?.name });
      } else {
        // Mark as attended
        await db.event_registrations()
          ?.update({ attended: true })
          .eq('event_id', eventId)
          .eq('qr_token', qrToken);
        setQrResult({ valid: true, name: reg.user?.name || 'Member', eventId });
        setAttendanceCounts(prev => ({ ...prev, [eventId]: (prev[eventId] || 0) + 1 }));
      }
    } catch (err) {
      setQrResult({ valid: false, error: err.message });
    }
    setTimeout(() => { setQrResult(null); setQrInput(''); }, 4000);
  };

  // Keep simulateScan as alias for manual input
  const simulateScan = () => validateQR(qrInput);


    return (
    <div>
      <SectionTitle action={
        <div style={{ display:'flex', gap:'0.5rem' }}>
          <button onClick={()=>setShowQR(v=>!v)} style={{ padding:'0.4rem 0.8rem', background: showQR ? 'rgba(78,201,148,0.15)' : 'transparent', border:`1px solid ${showQR ? 'rgba(78,201,148,0.4)' : G.border}`, borderRadius:8, color:showQR?G.green:G.muted, cursor:'pointer', fontFamily:G.sans, fontSize:'0.72rem', fontWeight:600, display:'flex', alignItems:'center', gap:'0.3rem' }}>
            📱 {tr('cml.qr_scanner')}
          </button>
          <button style={{ padding:'0.4rem 0.9rem', background:G.gold, border:'none', borderRadius:8, color:'#0A0A0A', cursor:'pointer', fontFamily:G.sans, fontSize:'0.72rem', fontWeight:700 }}>
            + {tr('cml.new_event')}
          </button>
        </div>
      }>
        {tr('cml.my_events')}
      </SectionTitle>

      {/* QR Scanner panel */}
      {showQR && (
        <div style={{ background:G.card, border:`1px solid ${G.green}44`, borderRadius:12, padding:'1.2rem', marginBottom:'1.2rem' }}>
          <div style={{ fontSize:'0.65rem', color:G.green, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.8rem' }}>📱 {tr('cml.qr_scanner')}</div>
          <p style={{ fontSize:'0.78rem', color:G.muted, marginBottom:'0.8rem', lineHeight:1.6 }}>{tr('cml.qr_scan_prompt')}</p>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <div style={{ flex:1 }}>
              <QRScannerCamera
                onScan={async (parsed) => { await validateQR(parsed || qrInput);
                  setQrResult({ valid:true, evtId:parsed.eventId, userId:parsed.userId });
                  setAttendanceCounts(prev => ({ ...prev, [parsed.eventId]: (prev[parsed.eventId]||0)+1 }));
                  setTimeout(() => { setQrResult(null); setQrInput(''); }, 3000);
                }}
                onClose={() => setShowQR(false)}
              />
            </div>
          </div>
          {qrResult && (
            <div style={{ marginTop:'0.7rem', padding:'0.6rem 0.9rem', background: qrResult.valid ? 'rgba(78,201,148,0.15)' : 'rgba(224,112,112,0.15)', border:`1px solid ${qrResult.valid ? 'rgba(78,201,148,0.4)' : 'rgba(224,112,112,0.4)'}`, borderRadius:8 }}>
              <span style={{ fontSize:'0.8rem', fontWeight:700, color: qrResult.valid ? G.green : G.red }}>
                {qrResult.valid ? tr('cml.qr_validated') : tr('cml.qr_invalid')}
              </span>
              {qrResult.valid && <span style={{ fontSize:'0.72rem', color:G.muted, marginLeft:'0.5rem' }}>Event {qrResult.evtId}</span>}
            </div>
          )}
        </div>
      )}

      {/* Event list */}
      <div style={{ display:'flex', flexDirection:'column', gap:'0.8rem' }}>
        {eventsFiltered.map(ev => {
          const type = types[ev.type] || types.apero;
          const present = attendanceCounts[ev.id] || ev.confirmed || 0;
        
    return (
            <div key={ev.id} style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'80px 1fr', gap:0 }}>
                <div style={{ position:'relative', overflow:'hidden' }}>
                  <img src={ev.image} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', filter:ev.status==='past'?'grayscale(50%)':'none' }}/>
                </div>
                <div style={{ padding:'0.8rem' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.2rem' }}>
                    <span style={{ fontSize:'0.65rem' }}>{type.icon}</span>
                    <span style={{ fontSize:'0.62rem', color:type.color, fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase' }}>{type.label}</span>
                    <span style={{ marginLeft:'auto', fontSize:'0.58rem', padding:'0.05rem 0.4rem', borderRadius:99, background: ev.status==='published'?'rgba(78,201,148,0.15)':ev.status==='past'?'rgba(255,255,255,0.06)':'rgba(201,168,76,0.1)', color:ev.status==='published'?G.green:ev.status==='past'?G.muted:G.gold }}>
                      {ev.status}
                    </span>
                  </div>
                  <div style={{ fontSize:'0.88rem', fontWeight:600, color:G.white, marginBottom:'0.2rem', fontFamily:G.serif }}>{ev.title}</div>
                  <div style={{ fontSize:'0.68rem', color:G.muted }}>{formatEventDate(ev.date)} · {ev.location}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.8rem', marginTop:'0.5rem' }}>
                    <span style={{ fontSize:'0.68rem', color:G.muted }}>{ev.registered}/{ev.capacity} registered</span>
                    {ev.status === 'past' && <span style={{ fontSize:'0.68rem', color:G.green }}>✓ {present} present</span>}
                    <span style={{ marginLeft:'auto', fontSize:'0.68rem', color:G.gold }}>
                      ${(ev.price * ev.registered * REV_SPLIT.events.cml).toFixed(0)} earned
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab: Notifications ────────────────────────────────────────────────────
function TabNotifications({ cml, users, lang, tr }) {
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [selectedLeaves, setSelectedLeaves] = useState(new Set());
  const [sent, setSent] = useState(false);
  const [history, setHistory] = useState([
    { id:1, date:'2025-04-10', subject:'🎉 Speed Dating — April 19', message:'Hey! Our next speed dating is April 19 at Café Resonance. Only 8 spots left!', criteria:2, reach:183, opens:71 },
    { id:2, date:'2025-03-28', subject:'🌿 Plant-based apéro recap', message:'What a night! 31 of you showed up. Next one: May 15.', criteria:1, reach:42, opens:39 },
  ]);

  const reach = selectedLeaves.size > 0 ? countMatches(users, [...selectedLeaves]) : cml.stats.membersActive;

  const handleSend = () => {
    if (!subject.trim() || !message.trim()) return;
    setHistory(prev => [{
      id: Date.now(), date: new Date().toISOString().split('T')[0],
      subject, message, criteria: selectedLeaves.size, reach, opens: 0,
    }, ...prev]);
    setSent(true);
    setTimeout(() => { setSent(false); setSubject(''); setMessage(''); setSelectedLeaves(new Set()); }, 2000);
  };


    return (
    <div>
      <SectionTitle>{tr('cml.notif_title')}</SectionTitle>

      {/* Compose */}
      <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1.2rem', marginBottom:'1.2rem' }}>
        <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.8rem' }}>{tr('cml.notif_compose')}</div>
        <input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Subject / title"
          style={{ width:'100%', marginBottom:'0.6rem', padding:'0.55rem 0.8rem', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:8, color:G.white, fontFamily:G.sans, fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}/>
        <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Message to your network…" rows={4}
          style={{ width:'100%', marginBottom:'1rem', padding:'0.55rem 0.8rem', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:8, color:G.white, fontFamily:G.sans, fontSize:'0.78rem', resize:'vertical', lineHeight:1.6, outline:'none', boxSizing:'border-box' }}/>

        {/* Targeting */}
        <CategoryTreePicker
          selected={selectedLeaves} onChange={setSelectedLeaves}
          lang={lang} users={users}
          title="Target audience (leave empty = all active members)"
          compact={true}
        />

        <button onClick={handleSend} disabled={!subject.trim()||!message.trim()}
          style={{ marginTop:'1rem', width:'100%', padding:'0.75rem', background:sent?G.green:(subject&&message)?G.gold:'rgba(255,255,255,0.1)', border:'none', borderRadius:8, color:(subject&&message)?'#0A0A0A':G.muted, fontFamily:G.sans, fontSize:'0.82rem', fontWeight:700, cursor:(subject&&message)?'pointer':'default', transition:'all 0.2s' }}>
          {sent ? tr('cml.notif_sent') : tr('cml.notif_send',{n:reach})}
        </button>
      </div>

      {/* History */}
      <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.6rem' }}>Sent history</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
        {history.map(n => (
          <div key={n.id} style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:10, padding:'0.8rem 1rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.25rem' }}>
              <div style={{ fontSize:'0.82rem', fontWeight:600, color:G.white }}>{n.subject}</div>
              <span style={{ fontSize:'0.6rem', color:G.muted, flexShrink:0, marginLeft:'0.5rem' }}>{n.date}</span>
            </div>
            <div style={{ fontSize:'0.7rem', color:G.muted, marginBottom:'0.4rem', lineHeight:1.5 }}>{n.message}</div>
            <div style={{ display:'flex', gap:'0.8rem', fontSize:'0.65rem' }}>
              <span style={{ color:G.muted }}>{n.criteria > 0 ? `${n.criteria} criteria · ` : 'All members · '}{n.reach} reached</span>
              {n.opens > 0 && <span style={{ color:G.green }}>✓ {n.opens} opened ({Math.round(n.opens/n.reach*100)}%)</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Sponsors ─────────────────────────────────────────────────────────
const PARTNERSHIP_TYPES = [
  { id:'venue_partner',  label:'Partenaire Venue',            icon:'🏛️', color:G.gold },
  { id:'event_sponsor',  label:'Commanditaire d\'événement',  icon:'📣', color:G.blue },
  { id:'member_offer',   label:'Offre membre exclusive',      icon:'🎯', color:G.green },
  { id:'first_date',     label:'Premier rendez-vous',         icon:'💑', color:'#A084C9' },
  { id:'circle_sponsor', label:'Parrainage de Cercle',        icon:'⭕', color:'#E07070' },
  { id:'cobranded',      label:'Événement co-brandé',         icon:'✨', color:'#9CA880' },
];

function TabSponsors({ cml, tr }) {
  const [sponsors, setSponsors] = useState(cml.sponsors);
  const [editing, setEditing]   = useState(null);
  const [showNew, setShowNew]   = useState(false);
  const [newSponsor, setNewSponsor] = useState({ name:'', category:'', status:'prospect', monthly_value:0, logo:'🤝', contact_email:'', notes:'', partnership_type:'venue_partner', circle_name:'' });

  const totalActive = sponsors.filter(s=>s.status==='active').reduce((t,sp)=>t+(sp.monthly_value||sp.monthlyValue||0),0);
  const pipeline    = sponsors.filter(s=>s.status!=='active').length;

  const statusColors = { prospect:G.muted, contacted:G.blue, signed:G.gold, active:G.green };
  const STAGES = ['prospect','contacted','signed','active'];

  const ptMeta = (id) => PARTNERSHIP_TYPES.find(p=>p.id===id) || PARTNERSHIP_TYPES[0];

  const saveEdit = async () => {
    const { db } = await import('../utils/supabase');
    const { partnership_type, circle_name, monthly_value, contact_email, notes, category, status } = editing;
    await db.sponsors?.()?.update({ partnership_type, circle_name, monthly_value: Number(monthly_value)||0, contact_email, notes, category, status }).eq('id', editing.id);
    setSponsors(prev => prev.map(s => s.id===editing.id ? editing : s));
    setEditing(null);
  };

  const saveNew = async () => {
    if (!newSponsor.name.trim()) return;
    const { db } = await import('../utils/supabase');
    const row = { ...newSponsor, cml_id: cml.id, network_id: cml.network, monthly_value: Number(newSponsor.monthly_value)||0 };
    const { data } = await db.sponsors?.()?.insert(row).select('*').single();
    if (data) setSponsors(prev => [data, ...prev]);
    setShowNew(false);
    setNewSponsor({ name:'', category:'', status:'prospect', monthly_value:0, logo:'🤝', contact_email:'', notes:'', partnership_type:'venue_partner', circle_name:'' });
  };


    return (
    <div>
      <SectionTitle action={
        <button onClick={()=>setShowNew(v=>!v)} style={{ padding:'0.4rem 0.9rem', background:G.gold, border:'none', borderRadius:8, color:'#0A0A0A', cursor:'pointer', fontFamily:G.sans, fontSize:'0.72rem', fontWeight:700 }}>
          + {tr('cml.add_sponsor')}
        </button>
      }>
        {tr('cml.sponsors_title')}
      </SectionTitle>

      {/* Summary */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.6rem', marginBottom:'1.2rem' }}>
        <StatCard label="Monthly value" value={`$${totalActive}`} sub="from active sponsors" color={G.gold}/>
        <StatCard label="Active sponsors" value={sponsors.filter(s=>s.status==='active').length} color={G.green}/>
        <StatCard label="In pipeline" value={pipeline} color={G.blue}/>
      </div>

      {/* Pipeline kanban */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.6rem', marginBottom:'1.2rem' }}>
        {STAGES.map(stage => {
          const stageSponors = sponsors.filter(s=>s.status===stage);
        
    return (
            <div key={stage} style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:10, padding:'0.7rem', minHeight:80 }}>
              <div style={{ fontSize:'0.58rem', fontWeight:700, color:statusColors[stage], textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem', display:'flex', justifyContent:'space-between' }}>
                {stage} <span style={{ background:'rgba(255,255,255,0.07)', borderRadius:99, padding:'0 0.3rem', color:G.muted }}>{stageSponors.length}</span>
              </div>
              {stageSponors.map(sp => {
                const pt = ptMeta(sp.partnership_type);
                return (
                <div key={sp.id} onClick={()=>setEditing(sp)} style={{ background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:7, padding:'0.45rem 0.55rem', marginBottom:'0.35rem', cursor:'pointer', transition:'border-color 0.15s' }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=statusColors[stage]+'66'}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=G.border}>
                  <div style={{ fontSize:'0.72rem', color:G.white, fontWeight:600 }}>{sp.logo||pt.icon} {sp.name}</div>
                  <div style={{ fontSize:'0.58rem', color:pt.color, marginTop:'0.1rem' }}>{pt.icon} {pt.label}</div>
                  {sp.circle_name && <div style={{ fontSize:'0.58rem', color:G.muted }}>⭕ {sp.circle_name}</div>}
                  {(sp.monthly_value||sp.monthlyValue) > 0 && <div style={{ fontSize:'0.62rem', color:G.gold, marginTop:'0.15rem' }}>${sp.monthly_value||sp.monthlyValue}/mo</div>}
                </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Edit sponsor */}
      {editing && (() => {
        const fieldStyle = { width:'100%', padding:'0.38rem 0.6rem', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:6, color:G.white, fontFamily:G.sans, fontSize:'0.75rem', outline:'none', boxSizing:'border-box' };
        const selectStyle = { ...fieldStyle, background:'#1A1726' };
        const lbl = (t) => <div style={{ fontSize:'0.58rem', color:G.muted, marginBottom:'0.2rem' }}>{t}</div>;
        return (
        <div style={{ background:G.card, border:`1px solid ${G.gold}44`, borderRadius:12, padding:'1.2rem', marginBottom:'1rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.8rem' }}>
            <div style={{ fontFamily:G.serif, fontSize:'1.2rem', color:G.white }}>{editing.name}</div>
            <button onClick={()=>setEditing(null)} style={{ background:'none', border:'none', color:G.muted, cursor:'pointer', fontSize:'1.1rem' }}>✕</button>
          </div>
          {/* Partnership type pills */}
          <div style={{ marginBottom:'0.8rem' }}>
            {lbl('Type de partenariat')}
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
              {PARTNERSHIP_TYPES.map(pt => (
                <button key={pt.id} onClick={()=>setEditing({...editing, partnership_type:pt.id})}
                  style={{ padding:'0.25rem 0.6rem', borderRadius:99, border:`1px solid ${editing.partnership_type===pt.id ? pt.color : G.border}`, background: editing.partnership_type===pt.id ? pt.color+'22' : 'transparent', color: editing.partnership_type===pt.id ? pt.color : G.muted, fontSize:'0.65rem', cursor:'pointer', fontFamily:G.sans }}>
                  {pt.icon} {pt.label}
                </button>
              ))}
            </div>
          </div>
          {/* Circle name — only for circle_sponsor */}
          {editing.partnership_type === 'circle_sponsor' && (
            <div style={{ marginBottom:'0.6rem' }}>
              {lbl('Nom du BeeCircle sponsorisé')}
              <input value={editing.circle_name||''} onChange={e=>setEditing({...editing,circle_name:e.target.value})}
                placeholder="ex: Bien-être & Yoga, Foodies MTL…" style={fieldStyle}/>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'0.6rem' }}>
            {[{l:'Catégorie',k:'category'},{l:'Contact (email)',k:'contact_email'},{l:'Valeur mensuelle ($)',k:'monthly_value'}].map(f=>(
              <div key={f.k}>
                {lbl(f.l)}
                <input value={editing[f.k]||''} onChange={e=>setEditing({...editing,[f.k]:e.target.value})} style={fieldStyle}/>
              </div>
            ))}
            <div>
              {lbl('Statut')}
              <select value={editing.status} onChange={e=>setEditing({...editing,status:e.target.value})} style={selectStyle}>
                {STAGES.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            {lbl('Notes')}
            <textarea value={editing.notes||''} onChange={e=>setEditing({...editing,notes:e.target.value})} rows={2}
              style={{ ...fieldStyle, resize:'none' }}/>
          </div>
          <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.7rem' }}>
            <button onClick={saveEdit}
              style={{ flex:1, padding:'0.5rem', background:G.gold, border:'none', borderRadius:7, color:'#0A0A0A', fontFamily:G.sans, fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
              Enregistrer
            </button>
            <button onClick={()=>setEditing(null)}
              style={{ padding:'0.5rem 0.8rem', background:'transparent', border:`1px solid ${G.border}`, borderRadius:7, color:G.muted, fontFamily:G.sans, fontSize:'0.72rem', cursor:'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
        );
      })()}

      {/* Add new sponsor form */}
      {showNew && (() => {
        const fieldStyle = { width:'100%', padding:'0.38rem 0.6rem', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:6, color:G.white, fontFamily:G.sans, fontSize:'0.75rem', outline:'none', boxSizing:'border-box' };
        const lbl = (t) => <div style={{ fontSize:'0.58rem', color:G.muted, marginBottom:'0.2rem' }}>{t}</div>;
        return (
        <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1.2rem', marginBottom:'1rem' }}>
          <div style={{ fontFamily:G.serif, fontSize:'1.1rem', color:G.white, marginBottom:'0.8rem' }}>Nouveau partenaire</div>
          <div style={{ marginBottom:'0.6rem' }}>
            {lbl('Nom du partenaire *')}
            <input value={newSponsor.name} onChange={e=>setNewSponsor({...newSponsor,name:e.target.value})} placeholder="Bar Le Lab…" style={fieldStyle}/>
          </div>
          <div style={{ marginBottom:'0.8rem' }}>
            {lbl('Type de partenariat')}
            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
              {PARTNERSHIP_TYPES.map(pt => (
                <button key={pt.id} onClick={()=>setNewSponsor({...newSponsor,partnership_type:pt.id})}
                  style={{ padding:'0.25rem 0.6rem', borderRadius:99, border:`1px solid ${newSponsor.partnership_type===pt.id ? pt.color : G.border}`, background: newSponsor.partnership_type===pt.id ? pt.color+'22' : 'transparent', color: newSponsor.partnership_type===pt.id ? pt.color : G.muted, fontSize:'0.65rem', cursor:'pointer', fontFamily:G.sans }}>
                  {pt.icon} {pt.label}
                </button>
              ))}
            </div>
          </div>
          {newSponsor.partnership_type === 'circle_sponsor' && (
            <div style={{ marginBottom:'0.6rem' }}>
              {lbl('Nom du BeeCircle')}
              <input value={newSponsor.circle_name||''} onChange={e=>setNewSponsor({...newSponsor,circle_name:e.target.value})} placeholder="ex: Foodies MTL…" style={fieldStyle}/>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'0.6rem' }}>
            <div>{lbl('Catégorie')}<input value={newSponsor.category} onChange={e=>setNewSponsor({...newSponsor,category:e.target.value})} placeholder="Venue, Wellness…" style={fieldStyle}/></div>
            <div>{lbl('Email contact')}<input value={newSponsor.contact_email} onChange={e=>setNewSponsor({...newSponsor,contact_email:e.target.value})} style={fieldStyle}/></div>
            <div>{lbl('Valeur mensuelle ($)')}<input type="number" value={newSponsor.monthly_value} onChange={e=>setNewSponsor({...newSponsor,monthly_value:e.target.value})} style={fieldStyle}/></div>
          </div>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={saveNew}
              style={{ flex:1, padding:'0.5rem', background:G.gold, border:'none', borderRadius:7, color:'#0A0A0A', fontFamily:G.sans, fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
              Ajouter
            </button>
            <button onClick={()=>setShowNew(false)}
              style={{ padding:'0.5rem 0.8rem', background:'transparent', border:`1px solid ${G.border}`, borderRadius:7, color:G.muted, fontFamily:G.sans, fontSize:'0.72rem', cursor:'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
        );
      })()}

      {/* Budget requests */}
      <div style={{ marginTop:'1.2rem' }}>
        <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.6rem' }}>Budget requests</div>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {cml.budgetRequests.map(br => (
            <div key={br.id} style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:9, padding:'0.7rem 0.9rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'0.78rem', color:G.white }}>{br.reason}</div>
                <div style={{ fontSize:'0.62rem', color:G.muted }}>{br.date}</div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
                <span style={{ fontSize:'0.82rem', fontWeight:700, color:G.gold }}>${br.amount}</span>
                <span style={{ fontSize:'0.6rem', padding:'0.1rem 0.4rem', borderRadius:99, background:br.status==='approved'?'rgba(78,201,148,0.15)':'rgba(255,255,255,0.05)', color:br.status==='approved'?G.green:G.muted }}>{br.status}</span>
              </div>
            </div>
          ))}
        </div>
        <button style={{ marginTop:'0.6rem', width:'100%', padding:'0.5rem', background:'transparent', border:`1px solid ${G.border}`, borderRadius:8, color:G.muted, fontFamily:G.sans, fontSize:'0.72rem', cursor:'pointer' }}>
          + {tr('cml.budget_request')}
        </button>
      </div>
    </div>
  );
}

// ── Tab: Stats ────────────────────────────────────────────────────────────
function TabStats({ cml, users, tr }) {
  const s = cml.stats;
  // Aggregated only — no individual user data
  const ageGroups = useMemo(() => {
    if (!users.length) return [];
    const groups = [{label:'18–24',min:18,max:24},{label:'25–29',min:25,max:29},{label:'30–35',min:30,max:35},{label:'36+',min:36,max:99}];
    return groups.map(g => ({ label:g.label, count:users.filter(u=>u.age>=g.min&&u.age<=g.max).length, pct:Math.round((users.filter(u=>u.age>=g.min&&u.age<=g.max).length/users.length)*100) }));
  }, [users]);

  const interestGroups = useMemo(() => {
    if (!users.length) return [];
    const counts = {};
    users.forEach(u => (u.interests||[]).forEach(i => { counts[i] = (counts[i]||0)+1; }));
    return Object.entries(counts).sort(([,a],[,b])=>b-a).slice(0,8).map(([k,v])=>({ key:k, label:k.replace('interest.','').replace(/_/g,' '), count:v }));
  }, [users]);


    return (
    <div>
      <SectionTitle>{tr('cml.stats_title')}</SectionTitle>

      {/* Main KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.7rem', marginBottom:'1.2rem' }}>
        <StatCard label={tr('cml.stats_members')} value={s.membersTotal} sub={`${s.membersActive} active (${Math.round(s.membersActive/s.membersTotal*100)}%)`} icon="🐝"/>
        <StatCard label={tr('cml.stats_retention')} value={`${s.retention}%`} sub="30-day retention" color={s.retention>70?G.green:G.gold}/>
        <StatCard label={tr('cml.stats_avg_score')} value={s.avgScore.toFixed(1)} sub="avg. profile score" color={G.purple}/>
        <StatCard label={tr('cml.stats_matches')} value={s.matchesMade} sub={`${(s.matchesMade/s.membersTotal*100).toFixed(1)}% of members`} color={G.blue}/>
      </div>

      {/* Age distribution */}
      <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.8rem' }}>Age distribution</div>
        {ageGroups.map(g => (
          <div key={g.label} style={{ display:'flex', alignItems:'center', gap:'0.7rem', marginBottom:'0.5rem' }}>
            <span style={{ width:36, fontSize:'0.72rem', color:G.muted }}>{g.label}</span>
            <div style={{ flex:1, height:6, background:'rgba(255,255,255,0.05)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${g.pct}%`, background:G.gold, borderRadius:3 }}/>
            </div>
            <span style={{ width:28, fontSize:'0.72rem', color:G.white, textAlign:'right' }}>{g.count}</span>
            <span style={{ width:28, fontSize:'0.62rem', color:G.muted, textAlign:'right' }}>{g.pct}%</span>
          </div>
        ))}
      </div>

      {/* Top interests */}
      <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1rem' }}>
        <div style={{ fontSize:'0.62rem', color:G.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.8rem' }}>Top interests in your network</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
          {interestGroups.map(ig => (
            <div key={ig.key} style={{ display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.25rem 0.6rem', background:'rgba(255,255,255,0.05)', border:`1px solid ${G.border}`, borderRadius:99 }}>
              <span style={{ fontSize:'0.7rem', color:G.white, textTransform:'capitalize' }}>{ig.label}</span>
              <span style={{ fontSize:'0.6rem', color:G.gold, fontWeight:700 }}>{ig.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Privacy note */}
      <div style={{ marginTop:'1rem', padding:'0.7rem 0.9rem', background:'rgba(91,155,213,0.05)', border:`1px solid rgba(91,155,213,0.15)`, borderRadius:8 }}>
        <p style={{ fontSize:'0.65rem', color:G.muted, margin:0, lineHeight:1.6 }}>
          🔒 <strong style={{color:G.white}}>Aggregated data only.</strong> These stats show community trends — no individual profiles, names, or personal data are accessible from this dashboard.
        </p>
      </div>
    </div>
  );
}

// ── Tab: Network Features ─────────────────────────────────────────────────
const NETWORK_FEATURE_DEFS = [
  { id:'events',     icon:'📅', labelKey:'feature.events',     descKey:'feature.events_desc' },
  { id:'circles',    icon:'🐝', labelKey:'feature.circles',    descKey:'feature.circles_desc' },
  { id:'beeconnect', icon:'🔗', labelKey:'feature.beeconnect', descKey:'feature.beeconnect_desc' },
  { id:'beehive',    icon:'🍯', labelKey:'feature.beehive',    descKey:'feature.beehive_desc' },
];

function TabNetworkFeatures({ cml }) {
  const { activeNetwork, networkFeatures, setNetworkFeatures } = useApp();
  const tr = useTr();
  const [saving, setSaving] = useState(null);
  const [localFeatures, setLocalFeatures] = useState(networkFeatures || {});

  // Sync local state when networkFeatures changes
  useEffect(() => { setLocalFeatures(networkFeatures || {}); }, [networkFeatures]);

  const toggle = async (feature) => {
    const newVal = !localFeatures[feature];
    setLocalFeatures(prev => ({ ...prev, [feature]: newVal }));
    setSaving(feature);
    try {
      await db.network_features()?.upsert({
        network_id: activeNetwork,
        feature,
        enabled: newVal,
        enabled_at: newVal ? new Date().toISOString() : null,
      }, { onConflict: 'network_id,feature' });
      setNetworkFeatures(prev => ({ ...prev, [feature]: newVal }));
    } catch (e) {
      // revert on error
      setLocalFeatures(prev => ({ ...prev, [feature]: !newVal }));
    }
    setSaving(null);
  };

  return (
    <div>
      <SectionTitle>{tr('feature.city_features') || 'City Features'}</SectionTitle>
      <div style={{ padding:'0.7rem 0.9rem', background:'rgba(201,168,76,0.06)', border:'1px solid rgba(201,168,76,0.18)', borderRadius:8, marginBottom:'1.2rem' }}>
        <p style={{ fontSize:'0.68rem', color:G.muted, margin:0, lineHeight:1.6 }}>
          {tr('feature.city_features_info') || 'Activate or deactivate features for your city. Changes take effect immediately for all members in your network.'}
        </p>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:'0.7rem' }}>
        {NETWORK_FEATURE_DEFS.map(f => {
          const enabled = localFeatures[f.id] === true;
          const isSaving = saving === f.id;
          return (
            <div key={f.id} style={{ background:G.card, border:`1px solid ${enabled ? 'rgba(201,168,76,0.35)' : G.border}`, borderRadius:12, padding:'0.9rem 1rem', display:'flex', alignItems:'center', gap:'0.8rem', transition:'border-color 0.2s' }}>
              <span style={{ fontSize:'1.35rem', flexShrink:0 }}>{f.icon}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:'0.8rem', fontWeight:600, color: enabled ? G.white : G.muted, fontFamily:G.sans }}>{tr(f.labelKey)}</div>
                <div style={{ fontSize:'0.65rem', color:G.muted, lineHeight:1.4, marginTop:'0.15rem' }}>{tr(f.descKey)}</div>
              </div>
              <button onClick={() => toggle(f.id)} disabled={isSaving}
                style={{ flexShrink:0, width:44, height:24, borderRadius:12, background: enabled ? G.gold : 'rgba(255,255,255,0.1)', border:`1px solid ${enabled ? G.gold : G.border}`, cursor: isSaving ? 'default' : 'pointer', position:'relative', transition:'all 0.2s', opacity: isSaving ? 0.6 : 1 }}>
                <div style={{ position:'absolute', top:2, left: enabled ? 22 : 2, width:18, height:18, borderRadius:'50%', background: enabled ? '#0A0A0A' : G.muted, transition:'left 0.2s' }}/>
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop:'1.5rem', padding:'0.7rem 0.9rem', background:'rgba(255,255,255,0.03)', border:`1px solid ${G.border}`, borderRadius:8 }}>
        <div style={{ fontSize:'0.62rem', color:G.muted, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'0.5rem' }}>Active network</div>
        <div style={{ fontSize:'0.78rem', color:G.white, fontWeight:600 }}>{activeNetwork}</div>
        <div style={{ fontSize:'0.62rem', color:G.muted, marginTop:'0.2rem' }}>
          {Object.entries(localFeatures).filter(([,v]) => v).map(([k]) => k).join(', ') || 'No features enabled'}
        </div>
      </div>
    </div>
  );
}

// ── Main CML Dashboard ────────────────────────────────────────────────────
export default function CMLDashboard() {
  const { currentUser, users, lang, setPage } = useApp();
  const tr  = useTr();
  const [tab,       setTab]       = useState('dashboard');
  const [pin,       setPin]       = useState('');
  const [unlocked,  setUnlocked]  = useState(false);

  // Admin preview mode: admin can view any CML's dashboard via sessionStorage
  const isAdmin      = currentUser?.role === 'admin';
  const previewCmlId = isAdmin ? (sessionStorage.getItem('cml_preview_id') || null) : null;
  const [previewUser,  setPreviewUser]  = useState(null);
  const [cmlRecord,    setCmlRecord]    = useState(null);

  // Revenue data
  const [revenueData, setRevenueData] = useState(null);
  const [events,      setEvents]      = useState([]);
  const [sponsors,    setSponsors]    = useState([]);

  // Tax report state
  const [taxYear,      setTaxYear]      = useState(new Date().getFullYear() - 1);
  const [taxLoading,   setTaxLoading]   = useState(false);
  const [taxData,      setTaxData]      = useState(null);
  const [showTaxModal, setShowTaxModal] = useState(false);

  // Admin auto-unlock
  useEffect(() => { if (isAdmin) setUnlocked(true); }, [isAdmin]);

  const targetId = previewCmlId || currentUser?.id;

  useEffect(() => {
    if (!targetId) return;
    import('../utils/supabase').then(({ db }) => {
      // Load the previewed user's profile if different from self
      if (previewCmlId && previewCmlId !== currentUser?.id) {
        db.user_core?.()?.select('*').eq('id', previewCmlId).single()
          .then(({ data }) => { if (data) setPreviewUser(data); });
      }
      // Load CML manager record (stage, network, payout info)
      db.cml_managers?.()?.select('*').eq('user_id', targetId).single()
        .then(({ data }) => { if (data) setCmlRecord(data); });
      // Load revenue rows
      db.cml_revenue_full?.()
        ?.select('*')
        .eq('cml_id', targetId)
        .order('period_start', { ascending: false })
        .limit(12)
        .then(({ data }) => { if (data) setRevenueData(data); });
      // Load events
      db.events_full?.()
        ?.select('*, event_registrations(count)')
        .eq('cml_id', targetId)
        .order('date', { ascending: false })
        .then(({ data }) => { if (data) setEvents(data); });
      // Load sponsors
      db.sponsors?.()
        ?.select('*')
        .eq('cml_id', targetId)
        .then(({ data }) => { if (data) setSponsors(data); });
    });
  }, [targetId]);

  const generateTaxReport = async () => {
    setTaxLoading(true);
    try {
      const base = process.env.REACT_APP_SUPABASE_URL
        ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1` : '';
      const { supabase } = await import('../utils/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${base}/cml/tax-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ taxYear, currency: currentUser?.currency || 'CAD' }),
      });
      const data = await res.json();
      if (!data.error) { setTaxData(data); setShowTaxModal(true); }
      else alert('Error generating report: ' + data.error);
    } catch (e) { alert('Error: ' + e.message); }
    setTaxLoading(false);
  };

  const printTaxReport = () => {
    if (!taxData) return;
    const { docType, year, cml: cmlInfo, payer, fields, payouts, totalPaid } = taxData;
    const html = generateTaxHTML(docType, year, cmlInfo, payer, fields, payouts, totalPaid);
    const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) setTimeout(() => { w.print(); URL.revokeObjectURL(url); }, 500);
    else URL.revokeObjectURL(url);
  };

  const CML_PIN = process.env.REACT_APP_CML_PIN || null;

  // ── Build composite cml object from DB data + fallbacks ──────────────────
  const baseUser    = previewUser || currentUser || {};
  const networkId   = cmlRecord?.network_id || baseUser.active_network || 'ca_montreal';
  const networkUsers = (users || []).filter(u => u.active_network === networkId);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400_000);
  const recentUsers   = networkUsers.filter(u => new Date(u.created_at) > thirtyDaysAgo);
  const activeUsers   = networkUsers.filter(u => u.last_active && new Date(u.last_active) > thirtyDaysAgo);

  // Aggregate monthly and all-time revenue from revenueData rows
  const latestRow  = revenueData?.[0] || {};
  const totalRow   = (revenueData || []).reduce((acc, r) => {
    ['events','sponsoring','subscriptions','alacarte'].forEach(k => { acc[k] = (acc[k]||0) + (r[k]||0); });
    return acc;
  }, {});

  const cml = {
    ...baseUser,
    id:           targetId,
    name:         baseUser.name || 'Community Manager',
    network:      networkId,
    networkLabel: cmlRecord?.network_label || networkId || 'My Network',
    stage:        cmlRecord?.stage || baseUser.stage || 'ambassador',
    revenue: {
      month: {
        events:        latestRow.events        || 312.50,
        sponsoring:    latestRow.sponsoring    || 195,
        subscriptions: latestRow.subscriptions || 48.20,
        alacarte:      latestRow.alacarte      || 22.50,
      },
      total: {
        events:        totalRow.events        || 1875,
        sponsoring:    totalRow.sponsoring    || 780,
        subscriptions: totalRow.subscriptions || 289.20,
        alacarte:      totalRow.alacarte      || 135,
      },
      pending:        cmlRecord?.pending_payout  || 187.50,
      nextPayoutDate: cmlRecord?.next_payout_date || '2025-05-01',
    },
    sponsors: sponsors.length > 0 ? sponsors : [
      { id:'sp1', name:'Bar Le Lab',     category:'Venue',     status:'active',    monthlyValue:150, logo:'🍺', contact:'marie@lelab.ca',    notes:'3 events booked for Q2' },
      { id:'sp2', name:'Café Résonance', category:'Venue',     status:'active',    monthlyValue:100, logo:'☕', contact:'info@resonance.ca',  notes:'Speed dating partner' },
      { id:'sp3', name:'Yoga Plateau',   category:'Wellness',  status:'signed',    monthlyValue:80,  logo:'🧘', contact:'allo@yogaplateau.ca', notes:'Co-branding, 2 events/qtr' },
      { id:'sp4', name:'Resto Le Petit', category:'F&B',       status:'contacted', monthlyValue:0,   logo:'🍽️', contact:'contact@lepetit.ca',  notes:'Meeting booked May 3' },
      { id:'sp5', name:'Vélo Montréal',  category:'Lifestyle', status:'prospect',  monthlyValue:0,   logo:'🚲', contact:'',                    notes:'Outdoor events potential' },
    ],
    budgetRequests: baseUser.budgetRequests || [
      { id:'br1', date:'2025-03-15', amount:500, reason:'Spring event venue deposit', status:'approved' },
      { id:'br2', date:'2025-04-02', amount:200, reason:'Social media ads — April',   status:'pending' },
    ],
    stats: {
      membersTotal:    networkUsers.length    || 247,
      membersActive:   activeUsers.length     || 183,
      membersNew:      recentUsers.length     || 22,
      retention:       networkUsers.length > 0
        ? Math.round(activeUsers.filter(u => new Date(u.created_at) < thirtyDaysAgo).length /
            Math.max(1, networkUsers.filter(u => new Date(u.created_at) < thirtyDaysAgo).length) * 100)
        : 74,
      avgScore:        baseUser.stats?.avgScore      || 7.8,
      matchesMade:     baseUser.stats?.matchesMade   || 89,
      eventsHosted:    events.filter(e => e.status === 'past').length || 8,
      conversionRate:  baseUser.stats?.conversionRate || 12,
    },
  };

  const TABS = [
    { id:'dashboard',     label:'Dashboard',     icon:'🏠' },
    { id:'features',      label:'Features',      icon:'⚡' },
    { id:'events',        label:'Events',        icon:'🎉' },
    { id:'notifications', label:'Notifications', icon:'📢' },
    { id:'sponsors',      label:'Sponsors',      icon:'🤝' },
    { id:'stats',         label:'Stats',         icon:'📊' },
    { id:'marketing',     label:'Marketing Kit', icon:'📄' },
  ];

  // ── Role + PIN lock screen ───────────────────────────────────────────────
  if (!unlocked) {
    const roleOk = currentUser?.role === 'cml' || currentUser?.role === 'admin';
    return (
      <div style={{ minHeight:'100vh', background:G.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem' }}>
        <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:16, padding:'2rem', width:'100%', maxWidth:360 }}>
          <div style={{ fontFamily:G.serif, fontSize:'2rem', marginBottom:'0.2rem', color:G.white }}>
            <span style={{ color:G.gold, fontStyle:'italic' }}>Bee</span>Love
          </div>
          <div style={{ fontSize:'0.72rem', color:G.muted, marginBottom:'1.5rem' }}>Community Manager Portal</div>
          {!roleOk ? (
            <p style={{ fontSize:13, color:'#E07070', textAlign:'center' }}>Access denied — CML role required.</p>
          ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
            {!CML_PIN && <p style={{ fontSize:12, color:'#E07070', margin:0 }}>REACT_APP_CML_PIN not configured</p>}
            <input type="password" value={pin} onChange={e => setPin(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && CML_PIN && pin === CML_PIN && setUnlocked(true)}
              placeholder="Enter your CML PIN"
              disabled={!CML_PIN}
              style={{ padding:'0.7rem', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:8, color:G.white, fontFamily:G.sans, outline:'none' }}
            />
            <button onClick={() => CML_PIN && pin === CML_PIN && setUnlocked(true)} disabled={!pin || !CML_PIN}
              style={{ padding:'0.7rem', background:(pin && CML_PIN) ? G.gold : 'rgba(255,255,255,0.08)', border:'none', borderRadius:8, color:(pin && CML_PIN) ? '#0A0A0A' : G.muted, cursor:(pin && CML_PIN) ? 'pointer' : 'default', fontFamily:G.sans, fontWeight:600 }}>
              Access dashboard
            </button>
          </div>
          )}
        </div>
      </div>
    );
  }

  // ── Main dashboard ───────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', background:G.bg, color:G.white, fontFamily:G.sans }}>

      {/* Admin preview banner */}
      {isAdmin && previewCmlId && (
        <div style={{ background:'rgba(201,168,76,0.12)', borderBottom:`1px solid rgba(201,168,76,0.3)`, padding:'0.5rem 1.2rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:'0.7rem', color:G.gold, fontFamily:G.sans }}>
            👁 Admin preview — viewing as <strong>{cml.name}</strong>
          </span>
          <button onClick={() => { sessionStorage.removeItem('cml_preview_id'); setPage?.('admin'); }}
            style={{ background:'none', border:`1px solid rgba(201,168,76,0.4)`, borderRadius:6, color:G.gold, fontSize:'0.65rem', cursor:'pointer', padding:'0.2rem 0.6rem', fontFamily:G.sans }}>
            ← Exit preview
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ position:'sticky', top:0, zIndex:100, background:G.bg, borderBottom:`1px solid ${G.border}`, padding:'0.75rem 1.2rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontFamily:G.serif, fontSize:'1.1rem', color:G.white }}>
            <span style={{ color:G.gold, fontStyle:'italic' }}>Bee</span>Love CML
          </div>
          <div style={{ fontSize:'0.62rem', color:G.muted }}>{cml.name} · {cml.networkLabel}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <div style={{ padding:'0.2rem 0.6rem', background:`${(STAGE_META[cml.stage]||STAGE_META.ambassador).color}22`, border:`1px solid ${(STAGE_META[cml.stage]||STAGE_META.ambassador).color}44`, borderRadius:99 }}>
            <span style={{ fontSize:'0.65rem', color:(STAGE_META[cml.stage]||STAGE_META.ambassador).color, fontWeight:700 }}>
              {(STAGE_META[cml.stage]||STAGE_META.ambassador).label}
            </span>
          </div>
          {!isAdmin && (
            <button onClick={() => setUnlocked(false)} style={{ background:'none', border:`1px solid ${G.border}`, borderRadius:6, color:G.muted, fontSize:'0.72rem', cursor:'pointer', padding:'0.3rem 0.7rem', fontFamily:G.sans }}>
              Log out
            </button>
          )}
        </div>
      </div>

      {/* Tab nav */}
      <div style={{ display:'flex', borderBottom:`1px solid ${G.border}`, overflowX:'auto', scrollbarWidth:'none' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flexShrink:0, padding:'0.7rem 1rem', background:'none', border:'none', borderBottom:`2px solid ${tab===t.id?G.gold:'transparent'}`, color:tab===t.id?G.gold:G.muted, fontSize:'0.75rem', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.3rem', fontFamily:G.sans }}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:'1.2rem', maxWidth:640, margin:'0 auto' }}>
        {tab === 'dashboard'     && <TabDashboard cml={cml} tr={tr} taxYear={taxYear} setTaxYear={setTaxYear} generateTaxReport={generateTaxReport} taxLoading={taxLoading}/>}
        {tab === 'features'      && <TabNetworkFeatures cml={cml}/>}
        {tab === 'events'        && <TabEvents cml={cml} lang={lang} tr={tr}/>}
        {tab === 'notifications' && <TabNotifications cml={cml} users={networkUsers} lang={lang} tr={tr}/>}
        {tab === 'sponsors'      && <TabSponsors cml={cml} tr={tr}/>}
        {tab === 'stats'         && <TabStats cml={cml} users={networkUsers} tr={tr}/>}
        {tab === 'marketing'     && <TabMarketing cml={cml} networkUsers={networkUsers}/>}
      </div>

      {/* Tax report modal */}
      {showTaxModal && taxData && (
        <div onClick={() => setShowTaxModal(false)}
          style={{ position:'fixed', inset:0, background:'var(--overlay-modal)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'#1a1a1a', border:'1px solid var(--gold-border)', borderRadius:16, padding:'1.5rem', maxWidth:480, width:'100%', maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'1rem' }}>
              <div>
                <p style={{ fontSize:'1rem', fontWeight:600, color:'var(--white)', margin:0 }}>{taxData.fields?.title}</p>
                <p style={{ fontSize:'0.72rem', color:'var(--muted)', margin:'0.2rem 0 0' }}>{taxData.fields?.subtitle}</p>
              </div>
              <button onClick={() => setShowTaxModal(false)} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.2rem' }}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'1rem' }}>
              {(taxData.fields?.fields||[]).map((f, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'0.5rem 0', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                  <span style={{ fontSize:'0.78rem', color:'var(--muted)' }}>{f.label}</span>
                  <span style={{ fontSize:'0.78rem', fontWeight:600, color:'var(--white)', textAlign:'right', maxWidth:'60%' }}>{f.value}</span>
                </div>
              ))}
            </div>
            {taxData.fields?.note && (
              <p style={{ fontSize:'0.7rem', color:'var(--muted)', lineHeight:1.6, padding:'0.6rem 0.8rem', background:'rgba(255,255,255,0.03)', borderRadius:8, marginBottom:'1rem' }}>
                ℹ️ {taxData.fields.note}
              </p>
            )}
            <button onClick={printTaxReport}
              style={{ width:'100%', padding:'0.8rem', background:'linear-gradient(135deg,#D4A84C,#B8922A)', border:'none', borderRadius:8, color:'#0A0A0A', fontFamily:'Outfit', fontWeight:700, fontSize:'0.9rem', cursor:'pointer' }}>
              🖨 Print / Save as PDF
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
